import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "../hooks/useLang";
import {
  createSession,
  patchSession,
  getSPRMap,
  getAudit,
  regenerateCell,
  listSessions,
  getSession,
  deleteSession,
  documentDownloadURL,
  finalizeSession,
  getFinalizeStatus,
  type ProductProfile,
  type ContactNature,
  type ContactDuration,
  type SPRMapResponse,
  type AuditReport,
  type FilledCell,
  type Session,
} from "../services/gspr";
import { listCustomers, type Customer as CustomerOption } from "../services/customers";
import { getSettings, getMe, type PaymentProvider } from "../services/api";
import { reconcilePayments, startCheckout } from "../services/payments";
import { listSavedProfiles, createSavedProfile, deleteSavedProfile, type SavedProfile } from "../services/savedProfiles";
import PaymentMethodModal from "./PaymentMethodModal";
import { indirmeAdiCoz, zamanDamgasi } from "../lib/download";

type TFn = (key: string) => string;

// BoolField — ProductProfile'ın boolean alanlarının adları. yesNo()/multi()
// yardımcıları yalnızca bu alanlarla çağrılabilsin diye daraltılmıştır.
type BoolField = {
  [K in keyof ProductProfile]-?: ProductProfile[K] extends boolean | undefined ? K : never;
}[keyof ProductProfile];

type Q = {
  key: string;
  titleKey: string;
  hintKey?: string;
  chapter: 1 | 2 | 3; // hangi GSPR Chapter'ina ait
  // num — bu sorunun akıştaki gösterim numarası (ör. "8"). Alt sorusu olan render'lar
  // bunu "8.2" gibi hiyerarşik alt-soru numarası üretmek için kullanır.
  render: (p: ProductProfile, set: (patch: Partial<ProductProfile>) => void, t: TFn, num: string) => React.ReactNode;
  isAnswered: (p: ProductProfile) => boolean;
};

/**
 * QUESTIONS — "GSPR Soru Listesi-V02.docx" (36 soru + 2 alt soru).
 *
 * Kural tablosu backend'dedir: go/internal/usecase/qa_flow/rules_v02.go.
 * Buradaki `key` alanları ProductProfile alan adlarıyla, `titleKey` önekleri de
 * V02 soru numaralarıyla eşleşir (v02_q18_* = dokümandaki Soru-18).
 *
 * İki yapısal not:
 *  1) Dizi chapter sıralı OLMAK ZORUNDA (I → II → III). activeQuestionsFor()
 *     bunu bir ön-ek olarak filtreler; sıra bozulursa kademeli açma kırılır.
 *  2) V02'nin koşullu soruları ayrı adım değil, ebeveyninin içinde gösterilir:
 *     Soru-9 → Soru-8'in altında, Soru-19 → Soru-18, Soru-21/26 → Soru-20,
 *     ALT SORU'lar → Soru-29 / Soru-30. Wizard adımı sayısı bu yüzden 36'dan azdır.
 */
const QUESTIONS: Q[] = [
  // ============ CHAPTER I ============
  {
    key: "intended_use_text",
    titleKey: "v02_q1_title",
    hintKey: "v02_q1_hint",
    chapter: 1,
    render: (p, set, t) => (
      <>
        <div className="opt-row">
          <button className={btn(p.intended_use === "medical")} onClick={() => set({ intended_use: "medical" })}>{t("v02_q1_o1")}</button>
          <button className={btn(p.intended_use === "aesthetic")} onClick={() => set({ intended_use: "aesthetic" })}>{t("v02_q1_o2")}</button>
        </div>
        {p.intended_use === "aesthetic" && (
          <div className="q1-aesthetic-confirm">
            <strong>⚠ {t("q1_annex_xvi_notice_title")}</strong>
            <p>{t("q1_annex_xvi_notice_body")}</p>
          </div>
        )}
      </>
    ),
    isAnswered: (p) => !!p.intended_use,
  },

  // ============ CHAPTER II ============
  {
    // Soru-2 + biyouyumluluk alt soruları (EN ISO 10993-1:2025 madde 6.4.4/6.4.2).
    // Alt sorular yalnız temas VARSA sorulur; temas yoksa SPR 10 zaten N/A'dır.
    // Cevaplar SPR 10 satırının standart atıflarını belirler — bkz.
    // go/internal/usecase/qa_flow/biocompatibility.go.
    key: "contact",
    titleKey: "v02_q2_title",
    chapter: 2,
    render: (p, set, t, num) => (
      <>
        <div className="opt-row">
          {/* Temas cevabı değişince alt cevaplar temizlenir (bkz. gspr.ts notu). */}
          <button className={btn(p.contact === "direct")} onClick={() => set({ contact: "direct", contact_natures: [], contact_duration: "" })}>{t("v02_q2_o1")}</button>
          <button className={btn(p.contact === "none")} onClick={() => set({ contact: "none", contact_natures: [], contact_duration: "" })}>{t("v02_q2_o2")}</button>
        </div>
        {p.contact === "direct" && (
          <>
            <p className="subq-title">{num}.2 {t("v02_q2b_title")}</p>
            <div className="opt-row">
              {CONTACT_NATURES.map(([v, labelKey]) => {
                const on = (p.contact_natures ?? []).includes(v);
                return (
                  <button
                    key={v}
                    className={btn(on)}
                    aria-pressed={on}
                    onClick={() => set({ contact_natures: toggleNature(p.contact_natures, v) })}
                  >
                    {t(labelKey)}
                  </button>
                );
              })}
            </div>
            <p className="subq-title">{num}.3 {t("v02_q2c_title")}</p>
            <div className="opt-row">
              {CONTACT_DURATIONS.map(([v, labelKey]) => (
                <button key={v} className={btn(p.contact_duration === v)} onClick={() => set({ contact_duration: v })}>
                  {t(labelKey)}
                </button>
              ))}
            </div>
          </>
        )}
      </>
    ),
    isAnswered: (p) => {
      if (!p.contact) return false;
      if (p.contact !== "direct") return true;
      return (p.contact_natures?.length ?? 0) > 0 && !!p.contact_duration;
    },
  },
  {
    key: "sterility",
    titleKey: "v02_q3_title",
    chapter: 2,
    render: (p, set, t, num) => (
      <>
        <div className="opt-row">
          <button className={btn(p.sterility === "sterile")} onClick={() => set({ sterility: "sterile" })}>{t("v02_q3_o1")}</button>
          <button className={btn(p.sterility === "pre_sterilization")} onClick={() => set({ sterility: "pre_sterilization" })}>{t("v02_q3_o2")}</button>
          <button className={btn(p.sterility === "non_sterile")} onClick={() => set({ sterility: "non_sterile" })}>{t("v02_q3_o3")}</button>
        </div>
        {(p.sterility === "sterile" || p.sterility === "pre_sterilization") && (
          <>
            <p className="subq-title">{num}.2 {t("v02_q3b_hint")}</p>
            <div className="opt-row">
              <button className={btn(p.sterilization_method === "eto")} onClick={() => set({ sterilization_method: "eto" })}>{t("q3b_eto")}</button>
              <button className={btn(p.sterilization_method === "radiation")} onClick={() => set({ sterilization_method: "radiation" })}>{t("q3b_radiation")}</button>
              <button className={btn(p.sterilization_method === "steam")} onClick={() => set({ sterilization_method: "steam" })}>{t("q3b_steam")}</button>
              <button className={btn(p.sterilization_method === "plasma")} onClick={() => set({ sterilization_method: "plasma" })}>{t("q3b_plasma")}</button>
              <button className={btn(p.sterilization_method === "aseptic")} onClick={() => set({ sterilization_method: "aseptic" })}>{t("q3b_aseptic")}</button>
            </div>
          </>
        )}
      </>
    ),
    isAnswered: (p) => {
      if (!p.sterility) return false;
      if ((p.sterility === "sterile" || p.sterility === "pre_sterilization") && !p.sterilization_method) return false;
      return true;
    },
  },
  yesNo("dual_sterile_non_sterile", "v02_q4", 2),
  yesNo("active", "v02_q5", 2),
  yesNo("active_implantable", "v02_q6", 2),
  yesNo("software", "v02_q7", 2),
  {
    // Soru-8 + Soru-9 (iyonize) — V02'de Soru-9 yalnızca Soru-8 evetse sorulur.
    key: "emits_radiation",
    titleKey: "v02_q8_title",
    chapter: 2,
    render: (p, set, t, num) => (
      <>
        <div className="opt-row">
          <button className={btn(p.emits_radiation === true)} onClick={() => set({ emits_radiation: true })}>{t("v02_q8_o1")}</button>
          <button className={btn(p.emits_radiation === false)} onClick={() => set({ emits_radiation: false })}>{t("v02_q8_o2")}</button>
        </div>
        {p.emits_radiation === true && (
          <>
            <p className="subq-title">{num}.2 {stripNum(t("v02_q9_title"))}</p>
            <div className="opt-row">
              <button className={btn(p.ionizing_radiation === true)} onClick={() => set({ ionizing_radiation: true })}>{t("v02_q9_o1")}</button>
              <button className={btn(p.ionizing_radiation === false)} onClick={() => set({ ionizing_radiation: false })}>{t("v02_q9_o2")}</button>
            </div>
          </>
        )}
      </>
    ),
    isAnswered: (p) => {
      if (p.emits_radiation === undefined) return false;
      if (p.emits_radiation === true && p.ionizing_radiation === undefined) return false;
      return true;
    },
  },
  {
    key: "substance_mode",
    titleKey: "v02_q10_title",
    chapter: 2,
    render: (p, set, t) => (
      <div className="opt-row">
        <button className={btn(p.substance_mode === "medicinal")} onClick={() => set({ substance_mode: "medicinal" })}>{t("v02_q10_o1")}</button>
        <button className={btn(p.substance_mode === "absorbed")} onClick={() => set({ substance_mode: "absorbed" })}>{t("v02_q10_o2")}</button>
        <button className={btn(p.substance_mode === "none")} onClick={() => set({ substance_mode: "none" })}>{t("v02_q10_o3")}</button>
      </div>
    ),
    isAnswered: (p) => !!p.substance_mode,
  },
  yesNo("implantable", "v02_q11", 2),
  yesNo("delivers_energy_or_substance", "v02_q12", 2),
  yesNo("measuring_function", "v02_q13", 2),
  multi("q14_group", "v02_q14", 2, [
    ["combination_use", "v02_q14_o1"],
    ["environmental_factors", "v02_q14_o2"],
    ["flammable_env", "v02_q14_o3"],
  ]),
  yesNo("requires_maintenance", "v02_q15", 2),
  multi("q16_group", "v02_q16", 2, [
    ["moving_parts", "v02_q16_o1"],
    ["vibration_noise", "v02_q16_o2"],
    ["dangerous_temp", "v02_q16_o3"],
  ]),
  yesNo("lay_person_use", "v02_q17", 2),
  yesNo("has_nanomaterial", "v02_q24", 2), // SPR 10.6 → Chapter II
  {
    key: "sharp_microbial",
    titleKey: "v02_q25_title",
    chapter: 2,
    render: (p, set, t) => (
      <div className="opt-col">
        <button className={btn(p.sharp_microbial === "sharp_and_microbial")} onClick={() => set({ sharp_microbial: "sharp_and_microbial" })}>{t("v02_q25_o1")}</button>
        <button className={btn(p.sharp_microbial === "sharp_only")} onClick={() => set({ sharp_microbial: "sharp_only" })}>{t("v02_q25_o2")}</button>
        <button className={btn(p.sharp_microbial === "microbial_only")} onClick={() => set({ sharp_microbial: "microbial_only" })}>{t("v02_q25_o3")}</button>
        <button className={btn(p.sharp_microbial === "none")} onClick={() => set({ sharp_microbial: "none" })}>{t("v02_q25_o4")}</button>
      </div>
    ),
    isAnswered: (p) => !!p.sharp_microbial,
  },
  {
    key: "biological_origin",
    titleKey: "v02_q27_title",
    chapter: 2,
    render: (p, set, t) => (
      <div className="opt-col">
        <button className={btn(p.biological_origin === "human_tissue")} onClick={() => set({ biological_origin: "human_tissue" })}>{t("v02_q27_o1")}</button>
        <button className={btn(p.biological_origin === "animal_tissue")} onClick={() => set({ biological_origin: "animal_tissue" })}>{t("v02_q27_o2")}</button>
        <button className={btn(p.biological_origin === "other_biological")} onClick={() => set({ biological_origin: "other_biological" })}>{t("v02_q27_o3")}</button>
        <button className={btn(p.biological_origin === "none")} onClick={() => set({ biological_origin: "none" })}>{t("v02_q27_o4")}</button>
      </div>
    ),
    isAnswered: (p) => !!p.biological_origin,
  },
  yesNo("contaminants_controlled", "v02_q28", 2),
  {
    // Soru-29 + ALT SORU — V02: "Bu soruya EVET yanıtı gelirse alt soru açılmalı".
    // İki alt cevap da SPR 10.3'ü Applicable yapar; fark yalnızca gerekçe metnindedir.
    key: "substance_interaction_verified",
    titleKey: "v02_q29_title",
    chapter: 2,
    render: (p, set, t, num) => (
      <>
        <div className="opt-row">
          <button className={btn(p.substance_interaction_verified === true)} onClick={() => set({ substance_interaction_verified: true })}>{t("v02_q29_o1")}</button>
          <button className={btn(p.substance_interaction_verified === false)} onClick={() => set({ substance_interaction_verified: false })}>{t("v02_q29_o2")}</button>
        </div>
        {p.substance_interaction_verified === true && (
          <>
            <p className="subq-title">{num}.2 {t("v02_q29sub_hint")}</p>
            <div className="opt-row">
              <button className={btn(p.administers_medicinal_products === true)} onClick={() => set({ administers_medicinal_products: true })}>{t("v02_q29sub_o1")}</button>
              <button className={btn(p.administers_medicinal_products === false)} onClick={() => set({ administers_medicinal_products: false })}>{t("v02_q29sub_o2")}</button>
            </div>
          </>
        )}
      </>
    ),
    isAnswered: (p) => {
      if (p.substance_interaction_verified === undefined) return false;
      if (p.substance_interaction_verified === true && p.administers_medicinal_products === undefined) return false;
      return true;
    },
  },
  {
    // Soru-30 + ALT SORU (ftalat) — V02: 1./2./3. seçenekte alt soru açılır.
    key: "cmr_substances",
    titleKey: "v02_q30_title",
    chapter: 2,
    render: (p, set, t, num) => (
      <>
        <div className="opt-col">
          <button className={btn(p.cmr_substances === "cmr")} onClick={() => set({ cmr_substances: "cmr" })}>{t("v02_q30_o1")}</button>
          <button className={btn(p.cmr_substances === "endocrine")} onClick={() => set({ cmr_substances: "endocrine" })}>{t("v02_q30_o2")}</button>
          <button className={btn(p.cmr_substances === "both")} onClick={() => set({ cmr_substances: "both" })}>{t("v02_q30_o3")}</button>
          <button className={btn(p.cmr_substances === "none")} onClick={() => set({ cmr_substances: "none" })}>{t("v02_q30_o4")}</button>
        </div>
        {(p.cmr_substances === "cmr" || p.cmr_substances === "endocrine" || p.cmr_substances === "both") && (
          <>
            <p className="subq-title">{num}.2 {t("v02_q30sub_hint")}</p>
            <div className="opt-row">
              <button className={btn(p.contains_phthalates === true)} onClick={() => set({ contains_phthalates: true })}>{t("v02_q30sub_o1")}</button>
              <button className={btn(p.contains_phthalates === false)} onClick={() => set({ contains_phthalates: false })}>{t("v02_q30sub_o2")}</button>
            </div>
          </>
        )}
      </>
    ),
    isAnswered: (p) => {
      if (!p.cmr_substances) return false;
      if (p.cmr_substances !== "none" && p.contains_phthalates === undefined) return false;
      return true;
    },
  },
  yesNo("biophysical_modeling", "v02_q31", 2),
  yesNo("mechanical_stress", "v02_q32", 2),
  yesNo("surface_critical", "v02_q33", 2),

  // ============ CHAPTER III ============
  {
    // Soru-18 + Soru-19 (format) — V02: format yalnızca kılavuz varsa sorulur.
    key: "has_ifu",
    titleKey: "v02_q18_title",
    hintKey: "v02_q18_hint",
    chapter: 3,
    render: (p, set, t, num) => (
      <>
        <div className="opt-row">
          <button className={btn(p.has_ifu === true)} onClick={() => set({ has_ifu: true })}>{t("v02_q18_o1")}</button>
          <button className={btn(p.has_ifu === false)} onClick={() => set({ has_ifu: false })}>{t("v02_q18_o2")}</button>
        </div>
        {p.has_ifu === true && (
          <>
            <p className="subq-title">{num}.2 {stripNum(t("v02_q19_title"))}</p>
            <div className="opt-row">
              <button className={btn(p.ifu_format === "paper")} onClick={() => set({ ifu_format: "paper" })}>{t("v02_q19_o1")}</button>
              <button className={btn(p.ifu_format === "eifu_only")} onClick={() => set({ ifu_format: "eifu_only" })}>{t("v02_q19_o2")}</button>
              <button className={btn(p.ifu_format === "hybrid")} onClick={() => set({ ifu_format: "hybrid" })}>{t("v02_q19_o3")}</button>
            </div>
          </>
        )}
      </>
    ),
    isAnswered: (p) => {
      if (p.has_ifu === undefined) return false;
      if (p.has_ifu === true && !p.ifu_format) return false;
      return true;
    },
  },
  {
    // Soru-20 + Soru-21 (yalnızca üretici) + Soru-26 (yenilenmiş tek kullanımlık).
    // V02'de ayrı numaralı ama koşullu sorular; tek adımda gösteriliyor.
    key: "reuse_mode",
    titleKey: "v02_q20_title",
    chapter: 3,
    render: (p, set, t, num) => (
      <>
        <div className="opt-row">
          <button className={btn(p.reuse_mode === "reusable")} onClick={() => set({ reuse_mode: "reusable" })}>{t("v02_q20_o1")}</button>
          <button className={btn(p.reuse_mode === "single_use")} onClick={() => set({ reuse_mode: "single_use" })}>{t("v02_q20_o2")}</button>
        </div>
        {p.reuse_mode === "reusable" && (
          <>
            <p className="subq-title">{num}.2 {stripNum(t("v02_q21_title"))}</p>
            <div className="opt-row">
              <button className={btn(p.reprocessed_by_manufacturer_only === true)} onClick={() => set({ reprocessed_by_manufacturer_only: true })}>{t("v02_q21_o1")}</button>
              <button className={btn(p.reprocessed_by_manufacturer_only === false)} onClick={() => set({ reprocessed_by_manufacturer_only: false })}>{t("v02_q21_o2")}</button>
            </div>
          </>
        )}
        {p.reuse_mode === "single_use" && (
          <>
            <p className="subq-title">{num}.2 {stripNum(t("v02_q26_title"))}</p>
            <div className="opt-row">
              <button className={btn(p.refurbished_single_use === true)} onClick={() => set({ refurbished_single_use: true })}>{t("v02_q26_o1")}</button>
              <button className={btn(p.refurbished_single_use === false)} onClick={() => set({ refurbished_single_use: false })}>{t("v02_q26_o2")}</button>
            </div>
          </>
        )}
      </>
    ),
    isAnswered: (p) => {
      if (!p.reuse_mode) return false;
      if (p.reuse_mode === "reusable" && p.reprocessed_by_manufacturer_only === undefined) return false;
      if (p.reuse_mode === "single_use" && p.refurbished_single_use === undefined) return false;
      return true;
    },
  },
  yesNo("multipack_single_ifu", "v02_q22", 3),
  yesNo("manufacturer_outside_eu", "v02_q23", 3),
  yesNo("special_storage_transport", "v02_q34", 3),
  yesNo("custom_made", "v02_q35", 3),
  yesNo("special_training_facility", "v02_q36", 3),
  // NOT: "İnhalasyon (Kural 20)" sınıflandırma sorusu müşteri isteğiyle kaldırıldı
  // (2026-07-27). inhalation_route artık her zaman false (Go bool sıfır değeri) →
  // Rule 20 uygulanmaz; inhalasyon cihazları manuel sınıf gerektirir.
];

// stripNum — başlık i18n metnindeki gömülü ön-numarayı ("8. ", "8.2 ", "18. ")
// temizler. Numara artık soru metnine gömülü değil; akıştaki POZİSYONDAN hesaplanıp
// (aktif liste dizinin ön-eki olduğundan pozisyon kararlıdır) ayrıca ekleniyor. Böylece
// adımlar her zaman sıralı (1,2,3…) görünür ve alt sorular ebeveyn.2 biçiminde numaralanır.
const stripNum = (s: string) => s.replace(/^\s*\d[\d.]*\s+/, "");

/**
 * yesNo — tek alanlı Evet/Hayır sorusu.
 *
 * Her soru KENDİ etiketlerini kullanır (`<keyPrefix>_o1` / `_o2`). V01'de 13 soru
 * q4'ün etiketlerini paylaşıyordu; bu yüzden "Cihaz nanomateryal içeriyor mu?"
 * sorusunun cevabı ekranda "Evet, aktif cihaz" görünüyordu.
 */
function yesNo(field: BoolField, keyPrefix: string, chapter: 1 | 2 | 3): Q {
  return {
    key: field,
    titleKey: `${keyPrefix}_title`,
    hintKey: `${keyPrefix}_hint`,
    chapter,
    render: (p, set, t) => (
      <div className="opt-row">
        <button className={btn(p[field] === true)} onClick={() => set({ [field]: true } as Partial<ProductProfile>)}>{t(`${keyPrefix}_o1`)}</button>
        <button className={btn(p[field] === false)} onClick={() => set({ [field]: false } as Partial<ProductProfile>)}>{t(`${keyPrefix}_o2`)}</button>
      </div>
    ),
    isAnswered: (p) => p[field] !== undefined,
  };
}

/**
 * multi — çoklu seçim sorusu (V02 Soru-14 ve Soru-16).
 *
 * Her seçenek bağımsız bir boolean'dır. "Hiçbiri" ayrı bir seçenek DEĞİLDİR:
 * üçüne birden "hayır" demek zaten hiçbiri demektir (V02 Soru-16'nın son
 * seçeneği bu yüzden kaldırıldı — tek işlevi SPR 20.4'ü NA yapmaktı ve
 * Soru-5 ile çakışıyordu). Soru "cevaplandı" sayılması için kullanıcının
 * hepsini açıkça işaretlemesi gerekir; bu yüzden her satır Evet/Hayır'dır.
 */
function multi(key: string, keyPrefix: string, chapter: 1 | 2 | 3, fields: [BoolField, string][]): Q {
  return {
    key,
    titleKey: `${keyPrefix}_title`,
    hintKey: `${keyPrefix}_hint`,
    chapter,
    render: (p, set, t) => (
      <div className="multi-group">
        {fields.map(([f, labelKey]) => (
          <div key={f} className="multi-row">
            <span className="multi-label">{t(labelKey)}</span>
            <div className="opt-row">
              <button className={btn(p[f] === true)} onClick={() => set({ [f]: true } as Partial<ProductProfile>)}>{t("v02_yes")}</button>
              <button className={btn(p[f] === false)} onClick={() => set({ [f]: false } as Partial<ProductProfile>)}>{t("v02_no")}</button>
            </div>
          </div>
        ))}
      </div>
    ),
    isAnswered: (p) => fields.every(([f]) => p[f] !== undefined),
  };
}

const btn = (active: boolean) => "opt" + (active ? " opt-active" : "");

/**
 * Biyouyumluluk alt sorusunun seçenekleri — EN ISO 10993-1:2025.
 *
 * Sıra standardın Table 1→4 sırasıdır (artan şiddet); değerler backend'deki
 * models.ContactNature / models.ContactDuration sabitleriyle birebir aynı olmak
 * ZORUNDA — eşleşmeyen bir değer sunucuda sessizce taban listeye düşer.
 */
const CONTACT_NATURES: [ContactNature, string][] = [
  ["intact_skin", "q2b_intact_skin"],
  ["mucosal", "q2b_mucosal"],
  ["breached_or_internal", "q2b_breached_internal"],
  ["circulating_blood", "q2b_blood"],
];

const CONTACT_DURATIONS: [ContactDuration, string][] = [
  ["limited", "q2c_limited"],
  ["prolonged", "q2c_prolonged"],
  ["long_term", "q2c_long_term"],
];

// toggleNature — çok seçimli listede bir kategoriyi ekler/çıkarır. Sonuç daima
// CONTACT_NATURES sırasındadır; tıklama sırası kaydedilen profili değiştirmesin
// (aynı cevap her zaman aynı JSON'u üretsin).
function toggleNature(cur: ContactNature[] | undefined, v: ContactNature): ContactNature[] {
  const set = new Set(cur ?? []);
  if (set.has(v)) set.delete(v); else set.add(v);
  return CONTACT_NATURES.map(([n]) => n).filter((n) => set.has(n));
}

type Preset = {
  key: string;
  label: string;
  emoji: string;
  hint: string;
  category: string;
  profile: ProductProfile;
};

const PRESETS: Preset[] = [
  // ============ Class I — non-sterile, passive ============
  {
    key: "wound_dressing",
    category: "Class I — Non-sterile",
    label: "Wound dressing (gauze)",
    emoji: "🩹",
    hint: "Non-sterile · passive · synthetic · no IFU",
    profile: {
      intended_use: "medical",
      intended_use_text: "Non-sterile knitted gauze used to absorb exudate from superficial exuding wounds.",
      contact: "direct", sterility: "non_sterile", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: false, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },
  {
    key: "exam_gloves",
    category: "Class I — Non-sterile",
    label: "Examination gloves (latex)",
    emoji: "🧤",
    hint: "Non-sterile · passive · skin contact",
    profile: {
      intended_use: "medical",
      intended_use_text: "Single-use latex examination glove protecting healthcare staff during general examination and low-risk clinical procedures.",
      contact: "direct", sterility: "non_sterile", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: false, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },

  // ============ Class IIa — sterile, passive ============
  {
    key: "surgical_syringe",
    category: "Class IIa — Sterile passive",
    label: "Surgical syringe",
    emoji: "💉",
    hint: "Sterile · single-use · passive",
    profile: {
      intended_use: "medical",
      intended_use_text: "Ready-to-use sterile single-use polypropylene syringe (luer-lock connector) used for parenteral drug administration.",
      contact: "direct", sterility: "sterile", sterilization_method: "eto", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },
  {
    key: "iv_catheter",
    category: "Class IIa — Sterile passive",
    label: "Intravenous catheter",
    emoji: "🩸",
    hint: "Sterile · vascular access · short-term invasive",
    profile: {
      intended_use: "medical",
      intended_use_text: "Ready-to-use sterile single-use IV catheter for establishing peripheral venous access and fluid/drug infusion.",
      contact: "direct", sterility: "sterile", sterilization_method: "eto", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },

  // ============ Class IIb — sterile, passive, local effect ============
  {
    key: "viscoelastic_ophthalmic",
    category: "Class IIb — Local effect",
    label: "Intraocular viscoelastic",
    emoji: "👁️‍🗨️",
    hint: "Sterile · ophthalmic surgery · sodium hyaluronate · Rule 21",
    profile: {
      intended_use: "medical",
      intended_use_text: "Sterile intraocular sodium hyaluronate viscoelastic used in ophthalmic surgery to maintain anterior chamber depth and protect ocular tissues (e.g. AriaVisc HA).",
      contact: "direct", sterility: "sterile", sterilization_method: "aseptic", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },
  {
    key: "hemodialysis_catheter",
    category: "Class IIb — Local effect",
    label: "Hemodialysis catheter",
    emoji: "🩻",
    hint: "Long-term invasive · sterile · vascular",
    profile: {
      intended_use: "medical",
      intended_use_text: "Sterile hemodialysis catheter providing temporary vascular access during hemodialysis sessions in patients with chronic renal failure.",
      contact: "direct", sterility: "sterile", sterilization_method: "eto", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },

  // ============ Class III — implant / drug / life-sustaining ============
  {
    key: "cardiac_pacemaker",
    category: "Class III — Active implant",
    label: "Implantable pacemaker",
    emoji: "🫀",
    hint: "Active implant · sterile · contains software",
    profile: {
      intended_use: "medical",
      intended_use_text: "Subcutaneously implanted active cardiac rhythm device that continuously monitors and paces the heart rhythm in adult patients with bradyarrhythmia.",
      contact: "direct", sterility: "sterile", sterilization_method: "eto", active: true, software: true,
      emits_radiation: false, biological_origin: "none", implantable: true,
      has_ifu: true, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },
  {
    key: "coronary_stent",
    category: "Class III — Passive implant",
    label: "Coronary stent (drug-eluting)",
    emoji: "🫀",
    hint: "Implant · contains drug · sterile · passive",
    profile: {
      intended_use: "medical",
      intended_use_text: "Sterile drug-eluting coronary stent (DES) releasing sirolimus, used to provide additional lumen expansion in coronary artery stenosis.",
      contact: "direct", sterility: "sterile", sterilization_method: "eto", active: false, software: false,
      // V02 ilaç etken maddesini biological_origin'den ayırıp Soru-10'a taşıdı:
      // sirolimus salan stent → substance_mode "medicinal" (SPR 12.1), doku kaynaklı değil.
      emits_radiation: false, biological_origin: "none", substance_mode: "medicinal", implantable: true,
      has_ifu: true, ifu_format: "paper", reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },
  {
    key: "hip_implant",
    category: "Class III — Passive implant",
    label: "Hip prosthesis (metal-on-poly)",
    emoji: "🦴",
    hint: "Long-term implant · sterile · passive · biocompatibility critical",
    profile: {
      intended_use: "medical",
      intended_use_text: "Sterile hip implant consisting of a femoral head and acetabular component with a metal-on-polyethylene articulation, used in total hip arthroplasty.",
      contact: "direct", sterility: "sterile", sterilization_method: "radiation", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: true,
      has_ifu: true, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },
  {
    key: "bone_graft_animal",
    category: "Class III — Biological",
    label: "Bone graft matrix (bovine)",
    emoji: "🧬",
    hint: "Animal tissue · sterile · implant",
    profile: {
      intended_use: "medical",
      intended_use_text: "Sterile implantable graft containing bovine-derived demineralized bone matrix, used for bone defect repair in orthopedic and dental surgery.",
      contact: "direct", sterility: "sterile", sterilization_method: "radiation", active: false, software: false,
      emits_radiation: false, biological_origin: "animal_tissue", implantable: true,
      has_ifu: true, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },

  // ============ SaMD — standalone medical software ============
  {
    key: "samd_retinopathy",
    category: "SaMD — Software",
    label: "Diabetic retinopathy screening AI",
    emoji: "🧠",
    hint: "SaMD · no contact · clinical decision support",
    profile: {
      intended_use: "medical",
      intended_use_text: "Standalone medical software (SaMD) that automatically detects diabetic retinopathy from retinal fundus images and provides decision support to the clinician.",
      contact: "none", sterility: "non_sterile", active: true, software: true,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "n/a", has_nanomaterial: false, inhalation_route: false,
    },
  },
  {
    key: "samd_dose_calc",
    category: "SaMD — Software",
    label: "Drug dose calculation software",
    emoji: "💊",
    hint: "SaMD · web/mobile · patient-specific dose",
    profile: {
      intended_use: "medical",
      intended_use_text: "Standalone medical software that calculates chemotherapy dosing for oncology patients based on body surface area, creatinine clearance and renal function.",
      contact: "none", sterility: "non_sterile", active: true, software: true,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "n/a", has_nanomaterial: false, inhalation_route: false,
    },
  },

  // ============ Active therapeutic / surgical ============
  {
    key: "surgical_laser",
    category: "Active therapeutic",
    label: "Surgical CO₂ laser",
    emoji: "🔬",
    hint: "Active · radiation (laser) · software-controlled",
    profile: {
      intended_use: "medical",
      intended_use_text: "Software-controlled CO₂ surgical laser device with adjustable power output, used for tissue cutting/coagulation in dermatology and general surgery.",
      contact: "direct", sterility: "non_sterile", active: true, software: true,
      emits_radiation: true, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "reusable", has_nanomaterial: false, inhalation_route: false,
      delivers_energy_or_substance: true, reprocessed_by_manufacturer_only: false,
    },
  },
  {
    key: "ecg_monitor",
    category: "Active diagnostic",
    label: "Bedside ECG monitor",
    emoji: "📈",
    hint: "Active · software · 12-lead · continuous monitoring",
    profile: {
      intended_use: "medical",
      intended_use_text: "Software-controlled bedside monitor that continuously tracks the patient's 12-lead ECG waveform and generates alarms in intensive care units.",
      contact: "direct", sterility: "non_sterile", active: true, software: true,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "reusable", has_nanomaterial: false, inhalation_route: false,
    },
  },

  // ============ Annex XVI — Aesthetic ============
  {
    key: "aesthetic_lens",
    category: "Annex XVI — Aesthetic",
    label: "Coloured contact lens",
    emoji: "👁️",
    hint: "Aesthetic · sterile · direct contact · CS 2022/2346",
    profile: {
      intended_use: "aesthetic",
      intended_use_text: "Purely aesthetic coloured contact lens placed on the cornea that does not correct any refractive error (MDR Annex XVI).",
      contact: "direct", sterility: "sterile", sterilization_method: "eto", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },
  {
    key: "ipl_hair_removal",
    category: "Annex XVI — Aesthetic",
    label: "IPL hair removal device",
    emoji: "✨",
    hint: "Aesthetic · intense pulsed light · radiation · Annex XVI",
    profile: {
      intended_use: "aesthetic",
      intended_use_text: "Aesthetic device designed for professional clinical use that disables unwanted hair follicles by selective thermolysis using intense pulsed light (IPL) (MDR Annex XVI).",
      contact: "direct", sterility: "non_sterile", active: true, software: true,
      emits_radiation: true, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "reusable", has_nanomaterial: false, inhalation_route: false,
      delivers_energy_or_substance: true, reprocessed_by_manufacturer_only: false,
    },
  },
  {
    key: "dermal_filler",
    category: "Annex XVI — Aesthetic",
    label: "Dermal filler injection",
    emoji: "💋",
    hint: "Aesthetic · sterile · deep contact · Annex XVI",
    profile: {
      intended_use: "aesthetic",
      intended_use_text: "Sterile aesthetic dermal filler containing hyaluronic acid and poly-L-lactic acid, injected intradermally/subcutaneously to augment facial contours (MDR Annex XVI).",
      contact: "direct", sterility: "sterile", sterilization_method: "aseptic", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },

  // ============ Dental / Orthopedic ============
  {
    key: "dental_implant",
    category: "Dental & Orthopedic",
    label: "Dental implant (titanium)",
    emoji: "🦷",
    hint: "Implant · sterile · titanium osseointegrated",
    profile: {
      intended_use: "medical",
      intended_use_text: "Sterile titanium endosseous dental implant placed in the alveolar bone to replace missing teeth, permanently fixed through osseointegration.",
      contact: "direct", sterility: "sterile", sterilization_method: "radiation", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: true,
      has_ifu: true, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },
  {
    key: "spinal_cage",
    category: "Dental & Orthopedic",
    label: "Spinal fusion cage",
    emoji: "🦴",
    hint: "Implant · sterile · PEEK/Ti spinal fusion",
    profile: {
      intended_use: "medical",
      intended_use_text: "Sterile PEEK/titanium cage placed in the intervertebral disc space for interbody spinal fusion in degenerative disc disease.",
      contact: "direct", sterility: "sterile", sterilization_method: "radiation", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: true,
      has_ifu: true, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },

  // ============ Surgical textiles & consumables ============
  {
    key: "surgical_suture",
    category: "Surgical consumable",
    label: "Absorbable suture (PGA)",
    emoji: "🧵",
    hint: "Sterile · intra-tissue · long-term invasive · bioresorbable",
    profile: {
      intended_use: "medical",
      intended_use_text: "Sterile PGA monofilament synthetic suture for soft-tissue approximation and ligation, absorbed through gradual hydrolytic degradation.",
      contact: "direct", sterility: "sterile", sterilization_method: "radiation", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },
  {
    key: "surgical_drape",
    category: "Surgical consumable",
    label: "Sterile surgical drape",
    emoji: "🟦",
    hint: "Sterile · passive · skin contact · IIa",
    profile: {
      intended_use: "medical",
      intended_use_text: "Ready-to-use sterile SMS non-woven surgical drape used to prevent contamination of the surgical field.",
      contact: "direct", sterility: "sterile", sterilization_method: "radiation", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: false, reuse_mode: "single_use", has_nanomaterial: false, inhalation_route: false,
    },
  },

  // ============ Anesthesia / Respiratory ============
  {
    key: "anesthesia_mask",
    category: "Anesthesia & Respiratory",
    label: "Anesthesia face mask",
    emoji: "😷",
    hint: "Sterile · passive · gas delivery · reusable",
    profile: {
      intended_use: "medical",
      intended_use_text: "Sterile silicone anesthesia mask anatomically fitting the patient's face for delivery of anesthetic gas or oxygen.",
      contact: "direct", sterility: "sterile", sterilization_method: "eto", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "reusable", has_nanomaterial: false, inhalation_route: false,
    },
  },
  {
    key: "ventilator",
    category: "Anesthesia & Respiratory",
    label: "ICU ventilator",
    emoji: "🫁",
    hint: "Active · software · life-support · IIb",
    profile: {
      intended_use: "medical",
      intended_use_text: "Pressure/volume-controlled, software-managed intensive care ventilator providing mechanical respiratory support to patients with acute respiratory failure.",
      contact: "direct", sterility: "non_sterile", active: true, software: true,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "reusable", has_nanomaterial: false, inhalation_route: false,
    },
  },

  // ============ Drug delivery ============
  {
    key: "infusion_pump",
    category: "Drug delivery",
    label: "Infusion pump",
    emoji: "💧",
    hint: "Active · software · direct IV delivery · IIb",
    profile: {
      intended_use: "medical",
      intended_use_text: "Software-controlled active infusion pump for clinical use that delivers intravenous fluids and drugs at a programmable dose and rate.",
      contact: "direct", sterility: "non_sterile", active: true, software: true,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "reusable", has_nanomaterial: false, inhalation_route: false,
    },
  },
  {
    key: "insulin_pen",
    category: "Drug delivery",
    label: "Insulin injection pen",
    emoji: "🖊️",
    hint: "Passive · sterile needle · diabetes · IIb",
    profile: {
      intended_use: "medical",
      intended_use_text: "Reusable mechanical pen device used at home by diabetic patients to deliver subcutaneous insulin injections at an adjustable dose.",
      contact: "direct", sterility: "non_sterile", active: false, software: false,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "reusable", has_nanomaterial: false, inhalation_route: false,
    },
  },

  // NOT: IVDR (in-vitro tani) ornek profilleri kaldirildi — sistem yalnizca MDR
  // (2017/745) uyumludur (musteri karari 2026-07-29).

  // ============ Imaging / Diagnostic ============
  {
    key: "ultrasound_probe",
    category: "Imaging & Diagnostic",
    label: "Ultrasound probe",
    emoji: "📡",
    hint: "Active · non-ionizing · direct contact · IIa",
    profile: {
      intended_use: "medical",
      intended_use_text: "Active piezoelectric transducer in direct contact with the patient's skin, providing linear/convex imaging for diagnostic ultrasonography.",
      contact: "direct", sterility: "non_sterile", active: true, software: true,
      emits_radiation: false, biological_origin: "none", implantable: false,
      has_ifu: true, reuse_mode: "reusable", has_nanomaterial: false, inhalation_route: false,
    },
  },
];

// Chapter mapping — her sorunun `chapter` alanindan turetilir (kullanici
// directive'i 2026-05-22, 2026-06-03 genisletildi):
//   - Ch I  = kullanim amaci → SPR 1-9
//   - Ch II = tasarim/uretim (temas, sterilite, ikili-arz, aktif, yazilim,
//             radyasyon, biyolojik, implant, enerji/madde) → SPR 10-22
//   - Ch III = etiket/IFU/reuse/nano/inhalasyon → SPR 23
// Sorular array'de chapter sirali tutulur (I→II→III) ki activeQuestions her
// zaman QUESTIONS'in bir on-eki kalsin; aksi halde kademeli acma bozulur.
function chapterOfStep(idx: number): 1 | 2 | 3 {
  return QUESTIONS[idx]?.chapter ?? 1;
}
// Bir chapter'in son soru indexi (sorular array'de chapter sirali oldugundan
// activeQuestions her zaman QUESTIONS'in bir on-eki kalir).
function lastIdxOfChapter(ch: 1 | 2 | 3): number {
  let last = 0;
  QUESTIONS.forEach((q, i) => {
    if (q.chapter === ch) last = i;
  });
  return last;
}

// Aşamalı kapsam — wizard ilk açılışta yalnızca Chapter I (Q1) gösterir.
// Kullanıcı Chapter I'i tamamlayınca "İleri →" ile bir sonraki chapter'ın
// soruları açılır (maxChapter state'i artar). Chapter'lar ardışık index
// aralıkları olduğundan (I=0, II=1..7, III=8..11) aktif sorular her zaman
// QUESTIONS'ın bir ön-eki kalır; step indeksleri geçerli kalır.
const INITIAL_MAX_CHAPTER: 1 | 2 | 3 = 1;

// Verili maxChapter'a göre aktif soru listesi (QUESTIONS'ın ön-eki).
function activeQuestionsFor(maxChapter: 1 | 2 | 3): Q[] {
  return QUESTIONS.filter((_, i) => chapterOfStep(i) <= maxChapter);
}

export default function GSPRWizard({ onFinalized }: { onFinalized?: () => void }) {
  const { t, lang } = useLang();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProductProfile>({});
  const [step, setStep] = useState(0);
  // Açık (erişilebilir) en yüksek chapter. Ch I varsayılan; "İleri →" ile artar.
  const [maxChapter, setMaxChapter] = useState<1 | 2 | 3>(INITIAL_MAX_CHAPTER);
  const [map, setMap] = useState<SPRMapResponse | null>(null);
  const [loading, setLoading] = useState(false);
  // Üretim sırasında geçen süre — modal "donmuş" hissi vermesin diye canlı sayaç
  // (müşteri değerlendirmesi 2026-07-28, madde 2 ve 5).
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!loading) { setElapsed(0); return; }
    const t0 = Date.now();
    const id = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(id);
  }, [loading]);
  const [err, setErr] = useState<string | null>(null);
  const [recentSessions, setRecentSessions] = useState<Session[]>([]);

  // Intro (kapak sayfasi) tamamlandi mi? Profile'da urun adi varsa intro gecmis demektir.
  const [introDone, setIntroDone] = useState(false);
  // Kapak ile sorular arasindaki ara adim ("Ürünü kısaca tanımlayın") gecildi mi.
  // Alan ZORUNLU DEGIL; bu bayrak yalnizca ekranin bir kez gosterilmesini saglar.
  // Devam eden bir oturum acildiginda gosterilmez (kullanicinin akisini kesmemek icin).
  const [describeDone, setDescribeDone] = useState(false);
  // Musteri listesi (intro/kapak dropdown icin). Firma kimligi artik Ayarlar >
  // Genel'de (org_name + logo); onu sentetik "__org__" secenegi olarak basa ekle.
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  useEffect(() => {
    Promise.all([
      listCustomers().then((r) => r.customers || []).catch(() => [] as CustomerOption[]),
      getSettings().catch(() => null),
    ]).then(([list, settings]) => {
      const opts: CustomerOption[] = [...list];
      if (settings?.org_name?.trim()) {
        opts.unshift({
          id: "__org__",
          name: settings.org_name,
          logo_data_url: settings.logo_data_url || "",
          created_at: "",
          updated_at: "",
        });
      }
      setCustomers(opts);
    });
  }, []);

  // Kredi bakiyesi — kredisi yoksa yalnız Chapter 1 önizlenebilir; sonraki
  // chapter'a geçiş kilitli, gate'te "Satın Al" çıkar. Kredi yalnız "Dokümanı
  // Oluştur"da düşer. Varsayılan 1 (yükleme bitene kadar kilitleme yapma).
  const [credit, setCredit] = useState<number>(1);
  // GSPR üretiminin kredi maliyeti (backend belirler, getMe ile gelir; varsayılan 1000).
  const [cost, setCost] = useState<number>(1000);
  const [meLoaded, setMeLoaded] = useState<boolean>(false);
  // Bakiye maliyeti karşılıyor mu? getMe yüklenene kadar kilitleme (backend zaten
  // gerçek maliyeti enforce eder); yüklenince credit >= cost gate'i uygulanır.
  const hasCredit = !meLoaded ? true : credit >= cost;
  // Ödeme yöntemleri + fiyat bilgisi — kredi kapısındaki "Kredi Yükle" butonu
  // CreditsPanel'deki ile AYNI modalı açar; iki ekranda iki farklı satın alma
  // deneyimi olmasın.
  const [payMethods, setPayMethods] = useState<PaymentProvider[]>(["garanti"]);
  const [unitUSD, setUnitUSD] = useState(0);
  const [usdTryRate, setUsdTryRate] = useState(0);
  const refreshCredit = useCallback(() => {
    // PayPal'da onaylayıp pencereyi kapatan kullanıcının bekleyen ödemesini kapat;
    // sonucu beklemeden bakiyeyi okuma, yoksa kredi bir tur geç görünür.
    reconcilePayments().then(() => getMe()).then((m) => {
      setCredit(m.credit_balance);
      if (m.price_gspr_generation_credits && m.price_gspr_generation_credits > 0) setCost(m.price_gspr_generation_credits);
      if (m.payment_providers?.length) setPayMethods(m.payment_providers);
      setUnitUSD(m.doc_price_usd ?? 0);
      setUsdTryRate(m.usd_try_rate ?? 0);
      setMeLoaded(true);
    }).catch(() => {/* sessiz */});
  }, []);
  useEffect(() => { refreshCredit(); }, [refreshCredit]);
  // Anlık satın alma (gate'ten): bir doküman değeri kadar kredi al.
  // Popup ödeme → payment:done → bakiye tazelenir.
  const buyPopupRef = useRef<Window | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [buying, setBuying] = useState<PaymentProvider | null>(null);
  const buyOne = useCallback(async (provider: PaymentProvider) => {
    setErr(null);
    // Popup'ı tıklama jestiyle SENKRON aç (await'ten sonra açılırsa tarayıcı engeller).
    const popup = window.open("about:blank", "medreg_pay", "width=480,height=760");
    if (!popup) { setErr(t("cr_popup_blocked")); setPickerOpen(false); return; }
    try {
      popup.document.write("<!doctype html><meta charset=utf-8><title>…</title><p style='font:15px sans-serif;padding:24px;color:#333'>Ödeme sayfasına yönlendiriliyorsunuz…</p>");
      popup.document.close(); // ÖNEMLİ: açık parse durumunda location ataması navigasyona dönmez
    } catch { /* ignore */ }
    buyPopupRef.current = popup;
    setBuying(provider);
    try {
      // Tek doküman değeri kadar kredi al (cost = GSPR üretim maliyeti, ör. 1000).
      const { pay_url } = await startCheckout(cost, provider);
      if (!pay_url) throw new Error("pay_url alınamadı");
      popup.location.replace(pay_url);
      setPickerOpen(false);
    } catch (e) {
      try { popup.close(); } catch { /* ignore */ }
      setPickerOpen(false);
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBuying(null);
    }
  }, [t, cost]);
  // openBuy — kredi kapısındaki tek giriş noktası. Birden fazla yöntem açıksa
  // seçim modalını açar; tek yöntemde modal tek seçenek gösterip boşuna bir
  // tıklama ekleyeceği için doğrudan başlatır.
  const openBuy = useCallback(() => {
    if (payMethods.length > 1) setPickerOpen(true);
    else void buyOne(payMethods[0] ?? "garanti");
  }, [payMethods, buyOne]);
  useEffect(() => {
    const onMsg = (e: MessageEvent) => { if (e.data === "payment:done") refreshCredit(); };
    const onFocus = () => { if (buyPopupRef.current) refreshCredit(); };
    window.addEventListener("message", onMsg);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("message", onMsg);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshCredit]);

  // Sayfa acildiginda son 8 oturumu cek (step 0 panelinde gostermek icin)
  useEffect(() => {
    listSessions(8).then((r) => setRecentSessions(r.sessions || [])).catch(() => {/* sessiz */});
  }, []);

  // Açık chapter'lara göre türev değerler.
  const activeQuestions = useMemo(() => activeQuestionsFor(maxChapter), [maxChapter]);
  const activeChapters = useMemo(
    () => ([1, 2, 3] as const).filter((c) => c <= maxChapter) as number[],
    [maxChapter],
  );
  // SPR haritasını açık chapter'lara indirger (özet sayılarını yeniden hesaplar).
  const scopeMap = useCallback(
    (m: SPRMapResponse): SPRMapResponse => {
      if (maxChapter >= 3) return m;
      let decisions = m.decisions.filter((d) => d.chapter <= maxChapter);
      // Chapter I önizlemesi: kullanıcı bu noktada TEK soru yanıtlamış oluyor ama
      // Chapter I 14 uygulanabilir madde içeriyor. Hem harita hem indirilen dosya
      // SPR 1-3 ile sınırlı — ikisi aynı kapsamı göstersin (müşteri değerlendirmesi
      // 2026-07-28, madde 3). Backend de aynı sınırı uyguluyor: gspr.go
      // chapter1PreviewMaxSPR. Kredi durumundan bağımsızdır.
      if (maxChapter === 1) {
        const topNum = (spr: string) => {
          const x = spr.match(/SPR\s+(\d+)/);
          return x ? parseInt(x[1], 10) : 999;
        };
        decisions = decisions.filter((d) => topNum(d.spr) <= 3);
      }
      const applicable = decisions.filter((d) => d.applicability === "applicable").length;
      return {
        ...m,
        decisions,
        summary: { total: decisions.length, applicable, not_applicable: decisions.length - applicable },
      };
    },
    [maxChapter],
  );

  const resumeSession = useCallback(async (sid: string) => {
    setErr(null);
    setMap(null); // önceki SPR haritası gösterilmesin (eski sınıflandırma bayatlıyor)
    try {
      const s = await getSession(sid);
      setSessionId(s.id);
      setProfile(s.profile || {});
      setIntroDone(!!(s.profile?.product_name));
      setDescribeDone(!!(s.profile?.product_name)); // devam eden oturumda ara adim tekrar sorulmaz
      // Daha önce açılmış chapter'ı cevaplanmış sorulardan geri getir.
      const prof = s.profile || {};
      const answeredIdx = QUESTIONS.map((q, i) => (q.isAnswered(prof) ? i : -1)).filter((i) => i >= 0);
      const mc = (answeredIdx.length ? chapterOfStep(Math.max(...answeredIdx)) : INITIAL_MAX_CHAPTER) as 1 | 2 | 3;
      setMaxChapter(mc);
      // Son cevaplanmis sorudan devam et (acik chapter kapsaminda)
      const aq = activeQuestionsFor(mc);
      const lastIdx = aq.findIndex((q) => !q.isAnswered(prof));
      setStep(lastIdx >= 0 ? lastIdx : aq.length - 1);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const deleteRecentSession = useCallback(async (sid: string) => {
    if (!confirm(t("recent_delete_confirm"))) return;
    setErr(null);
    try {
      await deleteSession(sid);
      setRecentSessions((prev) => prev.filter((s) => s.id !== sid));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  // Tüm oturumları temizle — onay sonrası hepsini tek tek siler (toplu endpoint yok).
  const clearAllSessions = useCallback(async () => {
    if (!confirm(t("recent_clear_all_confirm"))) return;
    setErr(null);
    try {
      const all = (await listSessions(500)).sessions || [];
      await Promise.allSettled(all.map((s) => deleteSession(s.id)));
      setRecentSessions([]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    // Restore from localStorage
    const saved = localStorage.getItem("gspr_session");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.id && parsed.profile) {
          setSessionId(parsed.id);
          setProfile(parsed.profile);
          const mc = ([1, 2, 3].includes(parsed.maxChapter) ? parsed.maxChapter : INITIAL_MAX_CHAPTER) as 1 | 2 | 3;
          setMaxChapter(mc);
          setStep(Math.min(parsed.step || 0, activeQuestionsFor(mc).length - 1));
          if (parsed.profile.product_name) {
            setIntroDone(true);
            setDescribeDone(true); // sayfa yenilendi — ara adim tekrar sorulmaz
          }
        }
      } catch {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    if (sessionId) {
      localStorage.setItem("gspr_session", JSON.stringify({ id: sessionId, profile, step, maxChapter }));
    }
  }, [sessionId, profile, step, maxChapter]);

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;
    const s = await createSession();
    setSessionId(s.id);
    return s.id;
  }, [sessionId]);

  const applyPatch = useCallback(async (patch: Partial<ProductProfile>) => {
    setErr(null);
    const next = { ...profile, ...patch };
    setProfile(next);
    try {
      const id = await ensureSession();
      await patchSession(id, patch);
    } catch (e) {
      // Eski/kayıp oturum (404): otomatik yeni oturum aç ve birikmiş profili
      // tekrar yaz — kullanıcı takılı kalmasın.
      if (e instanceof Error && /HTTP 404/.test(e.message)) {
        try {
          const s = await createSession();
          setSessionId(s.id);
          await patchSession(s.id, next);
          return;
        } catch (e2) {
          setErr(e2 instanceof Error ? e2.message : String(e2));
          return;
        }
      }
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [profile, ensureSession]);

  const applyPreset = useCallback(async (preset: Preset) => {
    setErr(null);
    setMap(null); // önceki SPR haritası kalmasın
    // Kapak alanlarini preset metadata'sindan otomatik turet — kullaniciyi
    // intro adimina geri gondermeyelim (preset = hizli yol).
    const cls = preset.profile.class || "?";
    const rule = preset.profile.rule || 0;
    const enriched: ProductProfile = {
      ...preset.profile,
      // Kapak alanlari: kullanici intro'da girdiyse KORU (saved profile teknik
      // cevaplari doldursun ama kapak bilgisi tekrar girilmesin); yoksa preset/
      // metadata'dan turet.
      product_name: profile.product_name?.trim() || preset.profile.product_name || preset.label,
      risk_class_display: profile.risk_class_display?.trim() || preset.profile.risk_class_display || `Class ${cls} — Rule ${rule}`,
      gspr_doc_code: profile.gspr_doc_code?.trim() || preset.profile.gspr_doc_code || "",
      customer_id: profile.customer_id ?? preset.profile.customer_id,
      customer_name: profile.customer_name ?? preset.profile.customer_name,
      customer_logo_data_url: profile.customer_logo_data_url ?? preset.profile.customer_logo_data_url,
      // V02'nin tüm soruları için varsayılan: preset belirtmediyse yaygın durum.
      // Hepsi doldurulmazsa isAnswered() false kalır ve "Bitir" kilitlenir.
      // Koşullu alanlar (alt sorular) yalnızca koşulu sağlanınca doldurulur;
      // aksi halde undefined kalmaları doğrudur — o soru hiç sorulmaz.
      dual_sterile_non_sterile: preset.profile.dual_sterile_non_sterile ?? false,
      active_implantable: preset.profile.active_implantable ?? false,
      substance_mode: preset.profile.substance_mode ?? "none",
      delivers_energy_or_substance: preset.profile.delivers_energy_or_substance ?? false,
      measuring_function: preset.profile.measuring_function ?? false,
      ionizing_radiation: preset.profile.emits_radiation ? (preset.profile.ionizing_radiation ?? false) : preset.profile.ionizing_radiation,
      // Soru-14 (çoklu seçim)
      combination_use: preset.profile.combination_use ?? false,
      environmental_factors: preset.profile.environmental_factors ?? false,
      flammable_env: preset.profile.flammable_env ?? false,
      requires_maintenance: preset.profile.requires_maintenance ?? false,
      // Soru-16 (çoklu seçim) — SPR 20.4 buraya değil Soru-5'e (active) bağlı
      moving_parts: preset.profile.moving_parts ?? false,
      vibration_noise: preset.profile.vibration_noise ?? false,
      dangerous_temp: preset.profile.dangerous_temp ?? false,
      lay_person_use: preset.profile.lay_person_use ?? false,
      sharp_microbial: preset.profile.sharp_microbial ?? "none",
      contaminants_controlled: preset.profile.contaminants_controlled ?? true,
      substance_interaction_verified: preset.profile.substance_interaction_verified ?? false,
      administers_medicinal_products: preset.profile.substance_interaction_verified
        ? (preset.profile.administers_medicinal_products ?? false)
        : preset.profile.administers_medicinal_products,
      cmr_substances: preset.profile.cmr_substances ?? "none",
      contains_phthalates:
        (preset.profile.cmr_substances ?? "none") !== "none"
          ? (preset.profile.contains_phthalates ?? false)
          : preset.profile.contains_phthalates,
      biophysical_modeling: preset.profile.biophysical_modeling ?? false,
      mechanical_stress: preset.profile.mechanical_stress ?? false,
      surface_critical: preset.profile.surface_critical ?? false,
      // Chapter III
      ifu_format: preset.profile.has_ifu ? (preset.profile.ifu_format ?? "paper") : preset.profile.ifu_format,
      reprocessed_by_manufacturer_only:
        preset.profile.reuse_mode === "reusable" ? (preset.profile.reprocessed_by_manufacturer_only ?? false) : preset.profile.reprocessed_by_manufacturer_only,
      refurbished_single_use:
        preset.profile.reuse_mode === "single_use" ? (preset.profile.refurbished_single_use ?? false) : preset.profile.refurbished_single_use,
      multipack_single_ifu: preset.profile.multipack_single_ifu ?? false,
      manufacturer_outside_eu: preset.profile.manufacturer_outside_eu ?? false,
      special_storage_transport: preset.profile.special_storage_transport ?? false,
      custom_made: preset.profile.custom_made ?? false,
      special_training_facility: preset.profile.special_training_facility ?? false,
    };
    setProfile(enriched);
    setMaxChapter(3); // preset tum sorulari doldurur -> tum chapter'lar acik
    setStep(activeQuestionsFor(3).length - 1);
    setIntroDone(true); // preset secildiyse intro tamamlanmis sayilir
    try {
      const id = await ensureSession();
      await patchSession(id, enriched);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [ensureSession, profile]);

  // Intro adimi "Ilerle" — kapak bilgileri kaydedilir + wizard'a gec.
  const onIntroProceed = useCallback(async () => {
    if (!profile.product_name?.trim()) {
      setErr(t("wizard_intro_product_name") + " — " + t("customers_name_required"));
      return;
    }
    setErr(null);
    try {
      const id = await ensureSession();
      const patch: Partial<ProductProfile> = {
        product_name: profile.product_name?.trim(),
        risk_class_display: profile.risk_class_display?.trim(),
        gspr_doc_code: profile.gspr_doc_code?.trim(),
        customer_id: profile.customer_id,
        customer_name: profile.customer_name,
        customer_logo_data_url: profile.customer_logo_data_url,
      };
      try {
        await patchSession(id, patch);
      } catch (e) {
        if (e instanceof Error && /HTTP 404/.test(e.message)) {
          // Eski oturum kaybolmuş — yenisini aç ve kapak bilgilerini yaz.
          const s = await createSession();
          setSessionId(s.id);
          await patchSession(s.id, patch);
        } else {
          throw e;
        }
      }
      setIntroDone(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [ensureSession, profile, t]);

  // Ara adimin "Ilerle"si. Metin ZORUNLU DEGIL — bos da olsa gecilir.
  // Kaydetme hatasi akisi durdurmaz: metin zaten opsiyonel, kullanicinin
  // sorulara gecisini bir ag hatasi yuzunden engellemek dogru olmaz.
  const onDescribeProceed = useCallback(async () => {
    setDescribeDone(true);
    const txt = (profile.intended_use_text || "").trim();
    try {
      const id = await ensureSession();
      await patchSession(id, { intended_use_text: txt });
    } catch (e) {
      console.error("urun tanimi kaydedilemedi", e);
    }
  }, [ensureSession, profile.intended_use_text]);

  const onFinish = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      let id = await ensureSession();
      // Harita sunucudaki sess.Profile'dan üretilir; tam profili garanti et.
      // Bayat/kayıp oturumda (404) yeni oturum açıp profili tekrar yaz.
      try {
        await patchSession(id, profile);
      } catch (e) {
        if (e instanceof Error && /HTTP 404/.test(e.message)) {
          const s = await createSession();
          setSessionId(s.id);
          id = s.id;
          await patchSession(s.id, profile);
        } else {
          throw e;
        }
      }
      const m = await getSPRMap(id);
      setMap(m);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [ensureSession, profile]);

  const reset = useCallback(() => {
    localStorage.removeItem("gspr_session");
    setSessionId(null);
    setProfile({});
    setStep(0);
    setMaxChapter(INITIAL_MAX_CHAPTER);
    setMap(null);
    setErr(null);
    setIntroDone(false);
    setDescribeDone(false);
  }, []);

  // Bir sonraki chapter'ı aç ve ilk sorusuna geç.
  const unlockNextChapter = useCallback(() => {
    setMaxChapter((c) => {
      const ch = chapterOfStep(step);
      const next = Math.min(ch + 1, 3) as 1 | 2 | 3;
      return (next > c ? next : c);
    });
    setStep(lastIdxOfChapter(chapterOfStep(step)) + 1);
  }, [step]);

  // Aktif chapter kapsami daraldiysa eski step'i sinir icine cek.
  useEffect(() => {
    if (step > activeQuestions.length - 1) setStep(activeQuestions.length - 1);
  }, [step, activeQuestions.length]);

  const q = activeQuestions[Math.min(step, activeQuestions.length - 1)];
  const done = useMemo(() => activeQuestions.every((x) => x.isAnswered(profile)), [profile, activeQuestions]);
  // TÜM chapter'ların TÜM soruları yanıtlandı mı? "GSPR Haritasını Üret" bunun
  // şartıdır (müşteri kararı 2026-07-29) — açık chapter'ların bitmesi yetmez.
  const allAnswered = useMemo(() => QUESTIONS.every((x) => x.isAnswered(profile)), [profile]);

  // Ödeme yöntemi modalı — sihirbazın BİRDEN FAZLA dönüş dalı var (harita görünümü
  // ve soru adımları) ve satın alma her ikisinden de tetiklenebiliyor. Tek element
  // olarak kurulup ilgili dalların hepsine basılır; yoksa harita görünümündeyken
  // "Kredi Yükle" tıklanır ama modal DOM'a hiç girmez.
  const picker = (
    <PaymentMethodModal
      open={pickerOpen}
      methods={payMethods}
      credits={cost}
      unitUSD={unitUSD}
      usdTryRate={usdTryRate}
      busy={buying}
      onPick={buyOne}
      onClose={() => setPickerOpen(false)}
    />
  );

  if (map) {
    return (
      <>
        <SPRMapView map={scopeMap(map)} sessionId={sessionId!} chapterScope={activeChapters} onReset={reset} onBackToEdit={() => setMap(null)} canFinalize={maxChapter === 3} hasCredit={hasCredit} onFinalized={onFinalized} onCreditRefresh={refreshCredit} onBuyCredit={openBuy} />
        {picker}
      </>
    );
  }

  // ---- Intro phase (kapak bilgileri) ----
  if (!introDone) {
    return (
      <IntroStep
        profile={profile}
        customers={customers}
        onChange={(patch) => setProfile((p) => ({ ...p, ...patch }))}
        onProceed={onIntroProceed}
        sessionId={sessionId}
        err={err}
        t={t as TFn}
      />
    );
  }

  // Kapak tamamlandi, sorulara gecmeden ONCE: urunun serbest metin tanimi.
  if (!describeDone) {
    return (
      <DescribeStep
        value={profile.intended_use_text || ""}
        onChange={(v) => setProfile((p) => ({ ...p, intended_use_text: v }))}
        onProceed={onDescribeProceed}
        t={t as TFn}
      />
    );
  }

  // İlk açılışta (step=0, profile boş) preset banner ipucu
  // Quick Start (hazır örnek profil kataloğu) şimdilik gizli tutuluyor.
  // Geri açmak için tek yeter: bu bayrağı true yap. Kod ve PRESETS verisi
  // olduğu gibi duruyor; applyPreset kayıtlı profiller tarafından da kullanılıyor.
  const SHOW_QUICK_START: boolean = false;
  const showPresetHint = step === 0 && !profile.intended_use && !sessionId;
  const curCh = chapterOfStep(step);
  const chLabelKey = curCh === 1 ? "wizard_chapter1_label" : curCh === 2 ? "wizard_chapter2_label" : "wizard_chapter3_label";
  const isLastOfChapter = step === lastIdxOfChapter(curCh);
  // Açık soruların sonundayız ve sıradaki bir chapter daha var mı?
  const isLastActiveQuestion = step >= activeQuestions.length - 1;
  const canUnlockNext = isLastActiveQuestion && curCh < 3;
  // Kümülatif önizleme: 1..curCh tüm chapter'lar tek DOCX'te birleştirilir.
  const cumulativeChapters = Array.from({ length: curCh }, (_, i) => i + 1);
  // Etiket: tüm chapter'ları açıkça listele (Ch 1, Ch 1-2, Ch 1-2-3) — aralık
  // yerine tam liste, kafa karışmasın.
  const cumulativeLabel = `Ch ${cumulativeChapters.join("-")}`;
  // Önizleme yalnızca o chapter'a kadarki tüm sorular yanıtlıysa anlamlı.
  const cumulativeReady = activeQuestions.filter((qq) => qq.chapter <= curCh).every((qq) => qq.isAnswered(profile));
  // "GSPR Haritasını Üret" görünürlük/etkinlik kapısı: normalde yalnız Chapter III
  // sonunda ve TÜM sorular yanıtlıyken. Kredisiz kullanıcı sonraki chapter'ı
  // açamadığından onun için Chapter I teaser haritası eskisi gibi üretilebilir.
  const showFinish = !canUnlockNext || !hasCredit;
  const finishReady = hasCredit ? allAnswered : done;

  return (
    <div className="wizard">
      {/* Chapter badge */}
      <div className="wizard-chapter-badge" style={{
        padding: "8px 12px", marginBottom: 12, borderRadius: 6,
        background: "var(--bg-mute)", borderLeft: "3px solid var(--accent)",
        fontSize: 13, fontWeight: 500,
      }}>
        {t(chLabelKey)}
      </div>

      {SHOW_QUICK_START && showPresetHint && (
        <div className="preset-hint-banner">
          {t("wizard_preset_banner").replace("{n}", String(PRESETS.length))}
          <button
            className="preset-hint-jump"
            onClick={() => {
              document.querySelector(".preset-accordion")?.scrollIntoView({ behavior: "smooth" });
              (document.querySelector(".preset-accordion") as HTMLDetailsElement | null)?.setAttribute("open", "");
            }}
          >
            {t("wizard_jump_examples")}
          </button>
        </div>
      )}
      {/* MANUEL WIZARD (üstte) — kullanıcı soruları kendisi cevaplar */}
      <div className="wizard-progress">
        {activeQuestions.map((qq, i) => (
          <button
            key={qq.key}
            className={"step-dot" + (i === step ? " step-current" : "") + (qq.isAnswered(profile) ? " step-done" : "")}
            onClick={() => setStep(i)}
            aria-label={`${t("wizard_aria_question")} ${i + 1}`}
            title={`${t("wizard_aria_question")} ${i + 1}`}
          >
            {i + 1}
          </button>
        ))}
      </div>

      <div className="wizard-card">
        <h3>{step + 1}. {stripNum(t(q.titleKey as Parameters<typeof t>[0]))}</h3>
        {/* Soru altındaki gri açıklama gösterilmiyor — yalnız soru + cevaplar
            kalsın (müşteri isteği, 2026-07-22). hintKey verisi metadata olarak
            duruyor; gerekirse tekrar açılabilir. */}
        {q.render(profile, applyPatch, t as TFn, String(step + 1))}
      </div>

      {err && <div className="error">{t("err_prefix")}: {err}</div>}

      <div className="wizard-nav">
        <button className="btn-sec" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>{t("wizard_back")}</button>
        <span className="step-count">{step + 1} / {activeQuestions.length}</span>
        {/* Chapter sonunda KÜMÜLATİF preview (1..curCh birleşik) — LLM'siz hızlı önizleme.
            Kredisiz devre dışı (anchor disable edilemez → button olarak göster). */}
        {isLastOfChapter && cumulativeReady && sessionId && (
          hasCredit ? (
            <a
              className="btn-sec"
              href={documentDownloadURL(sessionId, undefined, cumulativeChapters, true)}
              target="_blank"
              rel="noopener noreferrer"
              title={t("wizard_chapter_preview").replace("{chapter}", cumulativeLabel)}
            >
              📄 {t("wizard_chapter_preview").replace("{chapter}", cumulativeLabel)}
            </a>
          ) : (
            <button className="btn-sec" disabled title={t("wizard_no_credit_note")}>
              📄 {t("wizard_chapter_preview").replace("{chapter}", cumulativeLabel)}
            </button>
          )
        )}
        {!isLastActiveQuestion ? (
          <button className="btn-pri" disabled={!q.isAnswered(profile)} onClick={() => setStep((s) => s + 1)}>{t("wizard_next")}</button>
        ) : (
          <>
            {/* Sonraki chapter'i ac — Chapter III'e varana kadar ASIL ileri eylem budur. */}
            {canUnlockNext && (
              <button
                className="btn-pri"
                disabled={!q.isAnswered(profile) || !hasCredit}
                onClick={unlockNextChapter}
                title={!hasCredit ? t("wizard_no_credit_note") : undefined}
              >
                {t("wizard_unlock_next_chapter").replace("{chapter}", String(curCh + 1))} →
              </button>
            )}
            {/* "GSPR Haritasını Üret" YALNIZCA en sonda: tüm chapter'lar açılmış ve
                tüm sorular yanıtlanmış olmalı (müşteri kararı 2026-07-29). Chapter
                sonlarındaki kümülatif önizleme butonu yerinde kalır.
                Tek istisna: kredisiz kullanıcı sonraki chapter'ı açamadığı için
                Chapter I önizleme haritasını burada üretmeye devam eder. */}
            {showFinish && (
              <button
                className="btn-pri"
                disabled={!finishReady || loading}
                onClick={onFinish}
                title={!finishReady ? t("wizard_finish_locked") : undefined}
              >
                {loading ? t("wizard_calculating") : (
                  <>{t("wizard_finish")}</>
                )}
              </button>
            )}
          </>
        )}
      </div>

      {!hasCredit && (
        <div className="wizard-subscribe-note" style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-start" }}>
          <span>🔒 {t("wizard_no_credit_note")}</span>
          <button className="btn-pri" onClick={openBuy} disabled={buying !== null}>
            {buying ? t("wizard_buy_starting") : t("wizard_buy_credit")}
          </button>
        </div>
      )}

      <button
        className="btn-sec wizard-reset"
        title={t("wizard_reset_hint")}
        onClick={() => { if (window.confirm(t("wizard_reset_confirm"))) reset(); }}
      >
        ↺ {t("wizard_reset")}
      </button>

      {picker}

      {/* GSPR haritası üretilirken (LLM) belirgin modal — göze batan uyarı. */}
      {loading && (
        <div className="gen-modal-overlay" role="alertdialog" aria-busy="true">
          <div className="gen-modal">
            <div className="gen-modal-spinner" aria-hidden />
            <h3>{t("wizard_gen_modal_title")}</h3>
            <p>{t("wizard_gen_modal_body")}</p>
            <div className="gen-progress"><div className="gen-progress-bar" /></div>
            <p className="gen-modal-elapsed" aria-live="polite">
              {t("wizard_gen_modal_elapsed").replace("{s}", String(elapsed))}
            </p>
            <p className="gen-modal-hint">{t("wizard_gen_modal_hint")}</p>
          </div>
        </div>
      )}

      {/* PRESET PANELI (Quick Start) — SHOW_QUICK_START gizliyken render edilmez */}
      {SHOW_QUICK_START && <PresetAccordion presets={PRESETS} onPick={applyPreset} />}

      {/* KAYITLI PROFILLERIM: "Previous sessions"in hemen ustunde. Secince tum
          cevaplar otomatik dolar (kapak bilgileri korunur). */}
      <SavedProfilesSection
        defaultOpen
        disabled={!hasCredit}
        hint={t("saved_profiles_continue_hint")}
        onPick={(sp) => applyPreset({
          key: "saved-" + sp.id, category: "Saved", label: sp.name, emoji: "⭐", hint: "", profile: sp.profile,
        })}
      />

      {/* ONCEKI OTURUMLAR (varsa) */}
      {recentSessions.length > 0 && (
        <div className="recent-sessions">
          <div className="recent-head">
            <span>{t("recent_sessions")}</span>
            <button className="recent-clear-all" onClick={clearAllSessions} title={t("recent_clear_all")}>
              🗑 {t("recent_clear_all")}
            </button>
          </div>
          <ul className="recent-list">
            {recentSessions.slice(0, 6).map((s) => {
              const use = (s.profile?.intended_use_text || t("wizard_empty_use")).slice(0, 60);
              const cls = s.profile?.class || "?";
              const updated = s.updated_at ? new Date(s.updated_at).toLocaleString(lang === "en" ? "en-GB" : "tr-TR") : "";
              return (
                <li key={s.id} className="recent-row">
                  <button className="recent-item" onClick={() => resumeSession(s.id)} title={s.id}>
                    <code>{cls}</code>
                    <span className="recent-use">{use}</span>
                    <span className="recent-date">{updated}</span>
                  </button>
                  <button
                    className="recent-del"
                    onClick={() => deleteRecentSession(s.id)}
                    title={t("wizard_session_delete_title")}
                    aria-label={t("delete")}
                  >
                    ✕
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function PresetAccordion({ presets, onPick }: { presets: Preset[]; onPick: (p: Preset) => void }) {
  const { t } = useLang();
  const [open, setOpen] = useState<boolean>(false);
  const [openCat, setOpenCat] = useState<string | null>(null);

  const cats = Array.from(new Set(presets.map((p) => p.category)));

  return (
    <details className="preset-accordion" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>
        <span className="preset-summary-label">{t("wizard_preset_summary").replace("{n}", String(presets.length))}</span>
        <span className="hint preset-hint">{t("wizard_preset_summary_hint").replace("{n}", String(cats.length))}</span>
      </summary>
      <div className="preset-bar">
        {cats.map((cat) => {
          const isOpen = openCat === cat;
          return (
            <div key={cat} className="preset-category">
              <button
                type="button"
                className={"preset-cat-toggle" + (isOpen ? " preset-cat-toggle-open" : "")}
                onClick={() => setOpenCat(isOpen ? null : cat)}
              >
                <span>{isOpen ? "▾" : "▸"} {cat}</span>
                <span className="preset-cat-count">{presets.filter((p) => p.category === cat).length}</span>
              </button>
              {isOpen && (
                <div className="preset-grid">
                  {presets.filter((p) => p.category === cat).map((p) => (
                    <button
                      key={p.key}
                      className="preset-card"
                      onClick={() => onPick(p)}
                      title={p.hint}
                    >
                      <span className="preset-emoji" aria-hidden="true">{p.emoji}</span>
                      <span className="preset-label">{p.label}</span>
                      <span className="preset-meta">{p.hint}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </details>
  );
}

function ProfileSummary({ profile, t, canSave }: { profile: ProductProfile; t: (k: string) => string; canSave?: boolean }) {
  const items: Array<{ key: string; label: string; value: string }> = [];
  const tx = (val: string | undefined, fallback = "—") => val || fallback;

  if (profile.intended_use_text) {
    items.push({ key: "iut", label: t("profile_intended_use_text"), value: profile.intended_use_text });
  }
  items.push({
    key: "iu",
    label: t("profile_intended_use"),
    value: profile.intended_use === "medical" ? t("q1_opt_medical") :
           profile.intended_use === "aesthetic" ? t("q1_opt_aesthetic") : "—",
  });
  items.push({
    key: "co",
    label: t("profile_contact"),
    value: profile.contact === "direct" ? t("q2_opt_direct") :
           profile.contact === "none" ? t("q2_opt_none") : "—",
  });
  // Biyouyumluluk kategorisi — SPR 10'un standart atıflarını bu belirlediği için
  // özette görünür (sterilizasyon yöntemiyle aynı gerekçe).
  if (profile.contact_natures?.length) {
    items.push({
      key: "cnat",
      label: t("profile_contact_nature"),
      value: profile.contact_natures
        .map((n) => t((CONTACT_NATURES.find(([v]) => v === n)?.[1] ?? "") as Parameters<typeof t>[0]))
        .join(", "),
    });
  }
  if (profile.contact_duration) {
    items.push({
      key: "cdur",
      label: t("profile_contact_duration"),
      value: t((CONTACT_DURATIONS.find(([v]) => v === profile.contact_duration)?.[1] ?? "") as Parameters<typeof t>[0]),
    });
  }
  items.push({
    key: "st",
    label: t("profile_sterility"),
    value: profile.sterility === "sterile" ? t("q3_opt_sterile") :
           profile.sterility === "pre_sterilization" ? t("q3_opt_pre_sterile") :
           profile.sterility === "non_sterile" ? t("q3_opt_non_sterile") : "—",
  });
  if (profile.sterilization_method) {
    items.push({
      key: "stm",
      label: t("profile_sterilization_method"),
      value: profile.sterilization_method === "eto" ? "EtO (ISO 11135)" :
             profile.sterilization_method === "radiation" ? t("q3b_radiation") + " (ISO 11137)" :
             profile.sterilization_method === "steam" ? t("q3b_steam") + " (ISO 17665)" :
             profile.sterilization_method === "plasma" ? t("q3b_plasma") + " (ISO 14937)" :
             profile.sterilization_method === "aseptic" ? t("q3b_aseptic") + " (ISO 13408)" : "—",
    });
  }
  items.push({
    key: "ac",
    label: t("profile_active"),
    value: profile.active === true ? t("yes") : profile.active === false ? t("no") : "—",
  });
  items.push({
    key: "sw",
    label: t("profile_software"),
    value: profile.software === true ? t("yes") : profile.software === false ? t("no") : "—",
  });
  items.push({
    key: "rad",
    label: t("profile_radiation"),
    value: profile.emits_radiation === true ? t("yes") : profile.emits_radiation === false ? t("no") : "—",
  });
  items.push({
    key: "bio",
    label: t("profile_biological_origin"),
    value: profile.biological_origin === "none" ? t("q7_opt_none") :
           profile.biological_origin === "animal_tissue" ? t("q7_opt_animal") :
           profile.biological_origin === "human_tissue" ? t("q7_opt_human") :
           profile.biological_origin === "drug" ? t("q7_opt_drug") : "—",
  });
  items.push({
    key: "imp",
    label: t("profile_implantable"),
    value: profile.implantable === true ? t("yes") : profile.implantable === false ? t("no") : "—",
  });
  items.push({
    key: "ifu",
    label: t("profile_has_ifu"),
    value: profile.has_ifu === true ? t("yes") : profile.has_ifu === false ? t("no") : "—",
  });
  items.push({
    key: "ru",
    label: t("profile_reuse"),
    value: profile.reuse_mode === "single_use" ? t("q9_opt_single") :
           profile.reuse_mode === "reusable" ? t("q9_opt_reusable") :
           profile.reuse_mode === "n/a" ? "N/A" : "—",
  });
  // Suppress unused
  void tx;

  return (
    <details className="profile-summary">
      <summary>{t("profile_summary_title")}</summary>
      <dl className="profile-summary-list">
        {items.map((it) => (
          <div key={it.key} className="profile-summary-item">
            <dt>{it.label}</dt>
            <dd>{it.value}</dd>
          </div>
        ))}
      </dl>
      {canSave && <ProfileSaveBar profile={profile} t={t} />}
    </details>
  );
}

function ProfileSaveBar({ profile, t }: { profile: ProductProfile; t: (k: string) => string }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const onSave = async () => {
    if (!name.trim()) return;
    setSaving(true); setMsg(null);
    try {
      await createSavedProfile(name.trim(), profile);
      setMsg(t("saved_profile_success"));
      setName("");
      setTimeout(() => setMsg(null), 3500);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="profile-save-bar">
      <input
        type="text"
        className="profile-save-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t("saved_profile_name_ph")}
      />
      <button className="btn-pri" onClick={onSave} disabled={saving || !name.trim()}>
        {saving ? t("saving") : "⭐ " + t("saved_profile_save_btn")}
      </button>
      {msg && <span className="profile-save-msg">{msg}</span>}
    </div>
  );
}

function SavedProfilesSection({ onPick, defaultOpen, hint, disabled }: { onPick: (sp: SavedProfile) => void; defaultOpen?: boolean; hint?: string; disabled?: boolean }) {
  const { t, lang } = useLang();
  const [items, setItems] = useState<SavedProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(!!defaultOpen);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listSavedProfiles();
      setItems(r.profiles || []);
    } catch {
      // sessiz
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const onDel = async (id: string, name: string) => {
    if (!confirm(t("saved_profile_delete_confirm").replace("{name}", name))) return;
    try { await deleteSavedProfile(id); await load(); } catch { /* sessiz */ }
  };

  if (!loading && items.length === 0) return null;

  const locale = lang === "en" ? "en-GB" : "tr-TR";
  const fmtDate = (s: string) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" }); }
    catch { return s; }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? items.filter((sp) =>
        sp.name.toLowerCase().includes(q) ||
        (sp.profile.intended_use_text || "").toLowerCase().includes(q) ||
        (sp.profile.product_name || "").toLowerCase().includes(q)
      )
    : items;

  return (
    <details
      className="preset-accordion saved-profiles-accordion"
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary>⭐ {t("saved_profiles_title")} ({items.length})</summary>
      <div className="saved-profiles-inner">
        {hint && <p className="hint saved-profiles-hint">{hint}</p>}
        {disabled && <p className="saved-profiles-locked-note">🔒 {t("saved_profiles_locked_note")}</p>}
        <input
          type="search"
          className="saved-profiles-search"
          placeholder={t("saved_profiles_search_ph")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {filtered.length === 0 ? (
          <div className="hint" style={{ padding: "10px 4px" }}>{t("saved_profiles_no_match")}</div>
        ) : (
          <ul className="saved-profiles-list">
            {filtered.map((sp) => {
              const updated = sp.updated_at && sp.updated_at !== sp.created_at;
              return (
                <li key={sp.id} className={"saved-profile-row" + (disabled ? " saved-profile-row-locked" : "")}>
                  <button
                    type="button"
                    className="saved-profile-pick"
                    onClick={() => onPick(sp)}
                    disabled={disabled}
                    title={disabled ? t("saved_profiles_locked_note") : sp.name}
                  >
                    <div className="saved-profile-head">
                      <span className="saved-profile-name">⭐ {sp.name}</span>
                      <span className="saved-profile-meta">
                        <span title={t("saved_profile_created_at")}>📅 {fmtDate(sp.created_at)}</span>
                        {updated && (
                          <span title={t("saved_profile_updated_at")} style={{ marginLeft: 10 }}>
                            ✏️ {fmtDate(sp.updated_at)}
                          </span>
                        )}
                      </span>
                    </div>
                    {sp.profile.intended_use_text && (
                      <div className="saved-profile-sub">{sp.profile.intended_use_text.slice(0, 110)}</div>
                    )}
                  </button>
                  <button
                    type="button"
                    className="saved-profile-del"
                    onClick={(e) => { e.stopPropagation(); void onDel(sp.id, sp.name); }}
                    title={t("delete")}
                    aria-label={t("delete")}
                  >
                    ✕
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </details>
  );
}

// Uyum ifadesindeki (compliance statement) teknik-dosya yer tutucularini sadelestirir.
// {{TF05.006 Risk Analysis Report}} -> "Risk Analysis Report": ilk token bir dokuman
// kodu (nokta + rakam) gorunuyorsa atilir, yalnizca dokuman adi birakilir. Nihai
// DOCX'te ise kodun yerine "___________" bos cizgisi gelir (kullanici kendi kodunu
// yazar) — o donusum backend renderer'inda (renderTFBlank) yapilir.
function renderTFRefs(text: string): string {
  return text.replace(/\{\{\s*([^}]*?)\s*\}\}/g, (_m, inner: string) => {
    const s = String(inner).trim();
    const parts = s.match(/^(\S+)\s+([\s\S]+)$/);
    if (parts && /\./.test(parts[1]) && /\d/.test(parts[1])) return parts[2];
    return s;
  });
}

function SPRMapView({ map, sessionId, chapterScope, onReset, onBackToEdit, canFinalize, hasCredit, onFinalized, onCreditRefresh, onBuyCredit }: { map: SPRMapResponse; sessionId: string; chapterScope?: number[]; onReset: () => void; onBackToEdit: () => void; canFinalize?: boolean; hasCredit?: boolean; onFinalized?: () => void; onCreditRefresh?: () => void; onBuyCredit?: () => void }) {
  const { t, lang } = useLang();
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeErr, setFinalizeErr] = useState<string | null>(null);
  const [noCredit, setNoCredit] = useState(false);
  const [finalized, setFinalized] = useState(false);
  // "Generate GSPR Map" akışında tam doküman (canFinalize) + krediliyse sayfaya
  // gelir gelmez otomatik finalize edilir: 1 kredi düşer ve "Oluşturulan
  // Dokümanlar"a immutable kayıt eklenir. Backend idempotent olduğu için aynı
  // oturum ikinci kez finalize edilse de kredi TEKRAR düşmez (Back to edit → yeniden üret).
  // Bileşen sökülürse (unmount) devam eden polling setState yapmasın.
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);
  const doFinalize = useCallback(async () => {
    setFinalizing(true);
    setFinalizeErr(null);
    setNoCredit(false);
    try {
      const r = await finalizeSession(sessionId);
      if (r.status === "running") {
        // Backend üretimi arka plana aldı (gap analiziyle uzun sürüyor) — yokla.
        const deadline = Date.now() + 13 * 60 * 1000; // 13 dk üst sınır
        // eslint-disable-next-line no-constant-condition
        while (true) {
          await new Promise((res) => setTimeout(res, 2500));
          if (!aliveRef.current) return;
          const st = await getFinalizeStatus(sessionId);
          if (st.status === "done") break;
          if (st.status === "error") throw new Error(st.error || t("spr_finalize_saving"));
          if (Date.now() > deadline) throw new Error("timeout");
          // "running" | "idle" → beklemeye devam
        }
      }
      if (!aliveRef.current) return;
      setFinalized(true);
      onCreditRefresh?.(); // bakiye düştü — göstergeyi tazele
      // Doküman oluşturuldu → wizard taslağını temizle ki kullanıcı map'ten ayrılıp
      // YENİ GSPR başlattığında eski cevaplarla dolmasın (sıfırdan başlasın).
      // Mevcut map görünümü bellekteki state'ten gelir; yalnızca kalıcı taslak silinir.
      // maxChapter=3 (finalize koşulu) olduğundan parent'ın persist effect'i artık
      // tetiklenmez, silme kalıcıdır. "Back to edit" + cevap değişimi yeni taslak yazar.
      localStorage.removeItem("gspr_session");
    } catch (e) {
      if (!aliveRef.current) return;
      const msg = e instanceof Error ? e.message : String(e);
      if (msg === "insufficient_credit") { setNoCredit(true); setFinalizeErr(t("wizard_insufficient_credit")); }
      else setFinalizeErr(msg);
    } finally {
      if (aliveRef.current) setFinalizing(false);
    }
  }, [sessionId, onCreditRefresh, t]);
  // Sayfaya gelir gelmez bir kez otomatik finalize (çift çağrıyı ref ile engelle).
  const didAutoFinalize = useRef(false);
  useEffect(() => {
    if (didAutoFinalize.current) return;
    if (canFinalize && hasCredit) {
      didAutoFinalize.current = true;
      void doFinalize();
    }
  }, [canFinalize, hasCredit, doFinalize]);
  const [audit, setAudit] = useState<AuditReport | null>(null);
  const [auditing, setAuditing] = useState(false);
  const [auditErr, setAuditErr] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadErr, setDownloadErr] = useState<string | null>(null);
  const [expandedSPRs, setExpandedSPRs] = useState<Set<string>>(new Set());
  // Bölüm akordeonları kontrollü — "Tümünü genişlet" hem satır detaylarını hem
  // kapalı bölüm (Chapter II/III) akordeonlarını açar (yoksa detaylar gizli kalır).
  const presentChapters = [1, 2, 3].filter((ch) => map.decisions.some((d) => d.chapter === ch));
  const [openChapters, setOpenChapters] = useState<Set<number>>(new Set([1]));
  const setChapterOpen = useCallback((chap: number, open: boolean) => {
    setOpenChapters((prev) => {
      if (prev.has(chap) === open) return prev;
      const next = new Set(prev);
      if (open) next.add(chap); else next.delete(chap);
      return next;
    });
  }, []);
  const toggleSPR = useCallback((spr: string) => {
    setExpandedSPRs((prev) => {
      const next = new Set(prev);
      if (next.has(spr)) next.delete(spr); else next.add(spr);
      return next;
    });
  }, []);
  const expandAllSPRs = useCallback(() => {
    setExpandedSPRs(new Set(map.decisions.map((d) => d.spr)));
    setOpenChapters(new Set(presentChapters));
  }, [map.decisions, presentChapters]);
  const collapseAllSPRs = useCallback(() => {
    setExpandedSPRs(new Set());
  }, []);
  const allExpanded = expandedSPRs.size === map.decisions.length && map.decisions.length > 0;
  // Sadece bölüm (Chapter) akordeonlarını aç/kapat — satır detaylarına dokunmaz.
  const allChaptersOpen = presentChapters.length > 0 && presentChapters.every((ch) => openChapters.has(ch));
  const toggleAllChapters = useCallback(() => {
    setOpenChapters(allChaptersOpen ? new Set() : new Set(presentChapters));
  }, [allChaptersOpen, presentChapters]);

  const runAudit = useCallback(async (skipLLM: boolean) => {
    setAuditing(true);
    setAuditErr(null);
    try {
      // Saglayici Settings'teki varsayilana gore backend'de secilir.
      const r = await getAudit(sessionId, { skipLLM });
      setAudit(r);
    } catch (e) {
      setAuditErr(e instanceof Error ? e.message : String(e));
    } finally {
      setAuditing(false);
    }
  }, [sessionId]);

  // Sayfa terkinde uyari — uzun islem (audit veya download) suruyorsa
  useEffect(() => {
    const busy = auditing || downloading;
    if (!busy) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = t("busy_beforeunload");
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [auditing, downloading, t]);

  // .docx indirme — varsayilan saglayici (Settings) backend'de secilir; icerik
  // harita uretiminde olusturuldugu icin cache'ten gelir, yeniden AI'ya gitmez.
  const downloadDocx = useCallback(async () => {
    setDownloading(true);
    setDownloadErr(null);
    try {
      // Tam doküman (tüm chapter'lar açık = canFinalize) indirilirken chapter
      // filtresi GÖNDERİLMEZ; böylece backend ANNEX I - STANDARD–GSPR gap analizini
      // üretir (gap yalnızca chapter filtresi yokken çalışır — gspr.go). Kısmi/önizleme
      // indirmelerinde chapter scope korunur (hızlı, gap üretilmez).
      const dlChapters = canFinalize ? undefined : chapterScope;
      const res = await fetch(documentDownloadURL(sessionId, undefined, dlChapters));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Dosya adının TEK kaynağı sunucudur (handler/download_name.go):
      // {ÜRÜN}_GSPR_{YYYYMMDDHHMMSS}.docx. Burada blob indirdiğimiz için
      // tarayıcı Content-Disposition'a bakmaz, a.download ne derse onu yazar —
      // adı burada da kurarsak iki yer sapar ve biri güncellenip diğeri
      // unutulur. Başlıktan okunamazsa (vekil başlığı yutabilir) sunucunun
      // ürettiğiyle aynı biçimde bir yedek kurulur.
      a.download = indirmeAdiCoz(res.headers.get("Content-Disposition"))
        ?? `GSPR_${zamanDamgasi()}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloading(false);
    }
  }, [sessionId, chapterScope, canFinalize]);

  // Profili kaydet — mevcut profili isimle DB'ye yaz (sonra "kayitli profiller"den
  // secilip tum cevaplar otomatik dolsun, tekrar girilmesin).
  const [savingProfile, setSavingProfile] = useState(false);
  const [saveProfileMsg, setSaveProfileMsg] = useState<string | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);
  const onSaveProfile = useCallback(async () => {
    const def = map.profile?.product_name?.trim() || "";
    const name = window.prompt(t("saved_profile_name_ph"), def);
    if (name === null) return; // iptal
    const trimmed = name.trim();
    if (!trimmed) return;
    setSavingProfile(true);
    setSaveProfileMsg(null);
    try {
      await createSavedProfile(trimmed, map.profile);
      setProfileSaved(true);
      setSaveProfileMsg(t("saved_profile_success"));
      setTimeout(() => setSaveProfileMsg(null), 3500);
    } catch (e) {
      setSaveProfileMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingProfile(false);
    }
  }, [map.profile, t]);

  return (
    <div className="spr-view">
      <div className="spr-header">
        <div>
          <h2>{t("spr_title")}</h2>
          <p>
            <strong>{t("spr_classification")}:</strong>{" "}
            {map.profile?.risk_class_display?.trim()
              ? map.profile.risk_class_display
              : `Class ${map.classification.Class} — Rule ${map.classification.Rule} — ${map.classification.Reason}`}
          </p>
          <p>
            <strong>{t("spr_summary")}:</strong> {map.summary.total} {t("spr_total_items")} →{" "}
            <span className="badge-app">{map.summary.applicable} {t("spr_applicable_label")}</span>{" "}
            <span className="badge-na">{map.summary.not_applicable} {t("spr_na_label")}</span>
          </p>
          <p className="spr-chapter-breakdown">
            {[1, 2, 3].map((chap) => {
              const items = map.decisions.filter((d) => d.chapter === chap);
              if (items.length === 0) return null;
              const app = items.filter((d) => d.applicability === "applicable").length;
              const na = items.length - app;
              const romanIdx = chap - 1;
              const roman = ["I", "II", "III"][romanIdx];
              return (
                <span key={chap} className="chapter-breakdown-item">
                  <strong>{roman}:</strong> {items.length}{" "}
                  <span className="badge-app-mini">{app}A</span>
                  <span className="badge-na-mini">{na}N</span>
                </span>
              );
            })}
          </p>
          <ProfileSummary profile={map.profile} t={t} canSave />
        </div>
        <div className="spr-actions">
          <button
            className="btn-pri"
            disabled={downloading || auditing || finalizing || !hasCredit}
            onClick={() => downloadDocx()}
            title={!hasCredit ? t("wizard_no_credit_note") : finalizing ? t("spr_finalize_saving") : t("btn_docx_download_title")}
          >
            {downloading ? (
              <><span className="spinner-sm" /> {t("btn_docx_preparing")}</>
            ) : finalizing ? (
              <><span className="spinner-sm" /> {t("spr_finalize_saving")}</>
            ) : (
              <>{t("btn_docx_download")}</>
            )}
          </button>
          <button
            className="btn-sec"
            disabled={auditing || downloading || !hasCredit}
            onClick={() => runAudit(false)}
            title={!hasCredit ? t("wizard_no_credit_note") : t("btn_nb_audit_title")}
          >
            {auditing ? (
              <><span className="spinner-sm" /> {t("btn_nb_auditing")}</>
            ) : (
              <>{t("btn_nb_audit")}</>
            )}
          </button>
          <button
            className="btn-sec"
            disabled={auditing || downloading}
            onClick={onBackToEdit}
            title={t("btn_back_to_edit_title")}
          >
            {t("btn_back_to_edit")}
          </button>
          <button
            className={"btn-sec" + (profileSaved ? " spr-action-saved" : "")}
            disabled={auditing || downloading || savingProfile || profileSaved || !hasCredit}
            onClick={onSaveProfile}
            title={!hasCredit ? t("wizard_no_credit_note") : profileSaved ? t("btn_profile_saved") : t("btn_save_profile_title")}
          >
            {savingProfile ? (
              <><span className="spinner-sm" /> {t("saving")}</>
            ) : profileSaved ? (
              <>✓ {t("btn_profile_saved")}</>
            ) : (
              <>⭐ {t("btn_save_profile")}</>
            )}
          </button>
          <button
            className="btn-sec spr-action-reset"
            disabled={auditing || downloading}
            onClick={onReset}
            title={t("btn_new_profile_title")}
          >
            {t("btn_new_profile")}
          </button>
        </div>
      </div>

      {/* Finalize durum bandı — tam genişlik, header'ın altında (oval kenar) */}
      {canFinalize && (finalizing || finalized) && (
        <div className={"spr-finalize-banner" + (finalized ? " ok" : "")} title={t("wizard_finalize_hint")}>
          {finalizing ? (
            <><span className="spinner-sm" /> {t("spr_finalize_saving")}</>
          ) : (
            <>✅ {t("spr_finalize_saved")}
              {onFinalized && (
                <button className="btn-link spr-finalize-link" onClick={onFinalized}>{t("spr_view_in_documents")}</button>
              )}
            </>
          )}
        </div>
      )}
      {saveProfileMsg && <div className="profile-save-msg spr-save-profile-msg">{saveProfileMsg}</div>}
      {finalizeErr && (
        <div className="error" style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span>{finalizeErr}</span>
          {noCredit && onBuyCredit && (
            <button className="btn-pri" onClick={onBuyCredit}>{t("wizard_buy_credit")}</button>
          )}
        </div>
      )}
      <details className="spr-actions-help-accordion">
        <summary>❓ {t("spr_actions_help_title")}</summary>
        <div className="spr-actions-help">
          <p><strong>{t("btn_docx_download")}</strong> — {t("btn_docx_download_title")}</p>
          <p><strong>{t("btn_nb_audit")}</strong> — {t("btn_nb_audit_title")}</p>
          <p><strong>{t("btn_back_to_edit")}</strong> — {t("btn_back_to_edit_title")}</p>
          <p><strong>{t("btn_save_profile")}</strong> — {t("btn_save_profile_title")}</p>
          <p><strong>{t("btn_new_profile")}</strong> — {t("btn_new_profile_title")}</p>
        </div>
      </details>

      {map.generation_warning && (
        <div className="error" style={{ marginTop: 8 }}>⚠ {map.generation_warning}</div>
      )}

      {(auditing || downloading) && (
        <div className="busy-banner">{t("busy_banner")}</div>
      )}

      {auditErr && <div className="error">{t("err_prefix")} (audit): {auditErr}</div>}
      {downloadErr && <div className="error">{t("err_prefix")} (.docx): {downloadErr}</div>}
      {audit && <AuditView audit={audit} sessionId={sessionId} />}

      <div className="spr-table-toolbar no-print">
        <button
          className="btn-link-md"
          onClick={allExpanded ? collapseAllSPRs : expandAllSPRs}
          title={allExpanded ? t("spr_collapse_all_title") : t("spr_expand_all_title")}
        >
          {allExpanded ? "▾ " + t("spr_collapse_all") : "▸ " + t("spr_expand_all")}
        </button>
        <button
          className="btn-link-md"
          onClick={toggleAllChapters}
          title={allChaptersOpen ? t("spr_collapse_chapters_title") : t("spr_expand_chapters_title")}
        >
          {allChaptersOpen ? "▾ " + t("spr_collapse_chapters") : "▸ " + t("spr_expand_chapters")}
        </button>
        <small className="hint" style={{ marginLeft: "auto" }}>
          {expandedSPRs.size} / {map.decisions.length} {t("spr_expanded_count")}
        </small>
      </div>

      {(() => {
        const chapterLabels: Record<number, string> = {
          1: "CHAPTER I — GENERAL REQUIREMENTS",
          2: "CHAPTER II — REQUIREMENTS REGARDING DESIGN AND MANUFACTURE",
          3: "CHAPTER III — REQUIREMENTS REGARDING THE INFORMATION SUPPLIED WITH THE DEVICE",
        };
        // Sistem yalnizca MDR uyumlu (2026-07-29) — kaynak her zaman MDR Annex I.
        const regSource = t("spr_source_mdr");
        const renderRow = (d: typeof map.decisions[number]) => {
          const stds = (d.required_stds || []).join(", ");
          const hasJust = !!(d.justification && d.justification.trim());
          const isApplicable = d.applicability === "applicable";
          // "Detailed indication" = nihai DOCX ile aynı. Applicable: varsayılan AI
          // sağlayıcısının ürettiği metin (generated_text); yoksa (AI atlandıysa /
          // kaynak yetersizse) eski sabit placeholder. N/A: reddiye metni.
          const aiText = (d.generated_text || "").trim();
          const aiOk = aiText !== "" && aiText !== "INSUFFICIENT_SOURCES";
          const decisionRaw = isApplicable
            ? (aiOk ? aiText : (hasJust ? d.justification : t("spr_approval_text")))
            : d.justification;
          // {{TF05.NNN Ad}} yer tutucularini haritada sadece dokuman adi olarak goster.
          const decisionText = renderTFRefs(decisionRaw || "");
          const indication = decisionText || "—";
          const indicationTitle = decisionText || t("spr_indication_generated_hint");
          return (
          <React.Fragment key={d.spr}>
            <tr>
              <td className="spr-id-cell">
                <code>{d.spr}</code>
              </td>
              <td className="spr-clip" title={d.title || d.requirement_text || ""}>{d.title || d.requirement_text}</td>
              <td>
                {d.applicability === "applicable" ? (
                  <span className="badge-app">{t("spr_applicable_label")}</span>
                ) : (
                  <span className="badge-na">{t("spr_na_label")}</span>
                )}
              </td>
              <td className="spr-clip" title={stds || "—"}><small>{stds || "—"}</small></td>
              <td className="spr-clip" title={indicationTitle}><small className="just-text">{indication}</small></td>
              <td className="spr-clip" title={t("spr_td_chapter_hint")}>—</td>
              <td>
                {d.applicability === "applicable" ? (
                  <span className="spr-complied-yes">{t("spr_complied_yes")}</span>
                ) : (
                  <span className="spr-complied-no">{t("spr_complied_no")}</span>
                )}
              </td>
              <td className="spr-detail-toggle-cell">
                <button
                  className="spr-expand-caret"
                  onClick={() => toggleSPR(d.spr)}
                  title={t("spr_expand_title")}
                  aria-label={t("spr_expand")}
                  aria-expanded={expandedSPRs.has(d.spr)}
                >
                  {expandedSPRs.has(d.spr) ? "▾" : "▸"}
                </button>
              </td>
            </tr>
            {expandedSPRs.has(d.spr) && (
              <tr className="spr-detail-row">
                <td colSpan={8}>
                  <div className="spr-detail-content">
                    {d.requirement_text && (
                      <div className="spr-detail-decision">
                        <strong>{t("spr_detail_requirement")}</strong>
                        <p>{d.requirement_text}</p>
                      </div>
                    )}
                    <div className="spr-detail-regline">
                      <strong>{t("spr_source_rule_label")}:</strong> 📘 {regSource}
                    </div>
                    <div className="spr-detail-decision">
                      <strong>{isApplicable ? t("spr_detail_approval") : t("spr_detail_na_reason")}</strong>
                      <p>{decisionText}</p>
                    </div>
                    <div className="spr-detail-stds">
                      <strong>{t("spr_detail_required_stds")}</strong>
                      {(d.required_stds || []).length > 0 ? (
                        <ul className="spr-detail-list">
                          {(d.required_stds || []).map((s, i) => <li key={i}><code>{s}</code></li>)}
                        </ul>
                      ) : (
                        <p>—</p>
                      )}
                    </div>
                    <div className="spr-detail-haz">
                      <strong>{t("spr_detail_hazards")}</strong>
                      {(d.hazard_refs || []).length > 0 ? (
                        <ul className="spr-detail-list">
                          {(d.hazard_refs || []).map((h, i) => <li key={i}>{h}</li>)}
                        </ul>
                      ) : (
                        <p>—</p>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            )}
            {(d.sub_clauses || []).map((sc) =>
              renderRow({
                spr: `${d.spr} (${sc.letter})`,
                title: lang === "en" ? sc.text_en : sc.text_tr,
                chapter: d.chapter,
                applicability: sc.applicability,
                // Bent artık ana madde ile aynı iki alanı taşıyor: MDR Ek-I metni
                // (yönetmelikten) ve kendi uyum ifadesi (LLM). Eskiden ikisi de
                // yoktu; REQUIREMENT TEXT bloğu hiç çizilmiyor, uyum ifadesi de
                // placeholder'a düşüyordu (müşteri bulgusu 2026-07-29).
                requirement_text: sc.requirement_text || sc.text_en,
                generated_text: sc.generated_text,
                justification:
                  sc.justification ||
                  (sc.applicability === "not_applicable" ? d.justification : ""),
                required_stds: sc.applicability === "applicable" ? d.required_stds : [],
                hazard_refs: sc.applicability === "applicable" ? d.hazard_refs : [],
                source_rule: sc.source_rule || d.source_rule,
              })
            )}
          </React.Fragment>
          );
        };
        return ([1, 2, 3] as const).map((chap) => {
          const items = map.decisions.filter((d) => d.chapter === chap);
          if (items.length === 0) return null;
          const applicCount = items.filter((d) => d.applicability === "applicable").length;
          return (
            <details
              key={chap}
              className="spr-chapter-accordion"
              open={openChapters.has(chap)}
              onToggle={(e) => setChapterOpen(chap, (e.currentTarget as HTMLDetailsElement).open)}
            >
              <summary>
                <span className="spr-chapter-label">{chapterLabels[chap]}</span>
                <span className="spr-chapter-counts">
                  {items.length} SPR · <span className="badge-app-mini">{applicCount} A</span> / <span className="badge-na-mini">{items.length - applicCount} NA</span>
                </span>
              </summary>
              <div className="spr-table-scroll">
              <table className="spr-table">
                <colgroup>
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "23%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "22%" }} />
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "6%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t("spr_col_spr")}</th>
                    <th>{t("spr_col_title")}</th>
                    <th>{t("spr_col_status")}</th>
                    <th>{t("spr_col_standards")}</th>
                    <th>{t("spr_col_indication")}</th>
                    <th>{t("spr_col_td_chapter")}</th>
                    <th>{t("spr_col_complied")}</th>
                    <th>{t("spr_col_detail")}</th>
                  </tr>
                </thead>
                <tbody>{items.map(renderRow)}</tbody>
              </table>
              </div>
            </details>
          );
        });
      })()}
      <p className="spr-source-footnote">
        <small>{t("spr_source_footnote")}</small>
      </p>
    </div>
  );
}

type RegenState = "idle" | "busy" | "done" | "error";

function AuditView({ audit, sessionId }: { audit: AuditReport; sessionId: string }) {
  const { t } = useLang();
  const sevColor = (s: string) => s === "major" ? "sev-major" : s === "minor" ? "sev-minor" : "sev-obs";
  const score = audit.risk_score;
  const scoreColor = score < 20 ? "score-good" : score < 50 ? "score-mid" : "score-bad";

  // SPR no -> regenerasyon durumu ve sonucu
  const [regen, setRegen] = useState<Record<string, { state: RegenState; cell?: FilledCell; err?: string }>>({});

  const runRegen = useCallback(async (spr: string) => {
    setRegen((r) => ({ ...r, [spr]: { state: "busy" } }));
    try {
      const cell = await regenerateCell(sessionId, spr);
      setRegen((r) => ({ ...r, [spr]: { state: "done", cell } }));
    } catch (e) {
      setRegen((r) => ({ ...r, [spr]: { state: "error", err: e instanceof Error ? e.message : String(e) } }));
    }
  }, [sessionId]);

  // Tekil SPR'ları topla (ayni SPR icin birden cok bulgu olabilir — tek "yeniden uret" butonu)
  type Grouped = { spr: string; findings: typeof audit.findings extends (infer X)[] | null ? X[] : never[]; worstSev: number };
  const sevRank: Record<string, number> = { major: 0, minor: 1, observation: 2 };
  const grouped: Grouped[] = [];
  const idxBySPR: Record<string, number> = {};
  for (const f of audit.findings || []) {
    if (idxBySPR[f.spr] === undefined) {
      idxBySPR[f.spr] = grouped.length;
      grouped.push({ spr: f.spr, findings: [f], worstSev: sevRank[f.severity] ?? 99 });
    } else {
      const idx = idxBySPR[f.spr];
      grouped[idx].findings.push(f);
      const r = sevRank[f.severity] ?? 99;
      if (r < grouped[idx].worstSev) grouped[idx].worstSev = r;
    }
  }
  // Sirala: once severity (major > minor > observation), sonra SPR dogal numerik sira
  const cmpSPR = (a: string, b: string): number => {
    const as = a.replace(/^SPR\s+/i, "").split(".");
    const bs = b.replace(/^SPR\s+/i, "").split(".");
    const n = Math.min(as.length, bs.length);
    for (let i = 0; i < n; i++) {
      const am = as[i].match(/^(\d+)(.*)$/);
      const bm = bs[i].match(/^(\d+)(.*)$/);
      const an = am ? parseInt(am[1], 10) : 0;
      const bn = bm ? parseInt(bm[1], 10) : 0;
      if (an !== bn) return an - bn;
      const ar = am ? am[2] : as[i];
      const br = bm ? bm[2] : bs[i];
      if (ar !== br) return ar < br ? -1 : 1;
    }
    return as.length - bs.length;
  };
  grouped.sort((a, b) => a.worstSev !== b.worstSev ? a.worstSev - b.worstSev : cmpSPR(a.spr, b.spr));

  return (
    <div className="audit-view">
      <div className="audit-header">
        <h3>{t("audit_title")}</h3>
        <div className="audit-actions">
          <button
            className="btn-sec no-print"
            onClick={() => window.print()}
            title={t("btn_audit_print_title")}
            style={{ height: "fit-content", alignSelf: "center" }}
          >
            {t("btn_audit_print")}
          </button>
          <div className={`audit-score ${scoreColor}`}>
            <div className="score-num">{score}</div>
            <div className="score-label">{t("audit_risk_suffix")}</div>
          </div>
        </div>
      </div>
      <p className="audit-meta">
        {audit.cell_count} {t("audit_cells_scanned")} · {audit.llm_used ? t("audit_llm_active") : t("audit_llm_off")} ·
        {" "}<span className="sev-major">{audit.summary.major} MAJOR</span> ·
        {" "}<span className="sev-minor">{audit.summary.minor} MINOR</span> ·
        {" "}<span className="sev-obs">{audit.summary.observation} OBSERVATION</span>
      </p>
      {grouped.length > 0 ? (
        <ul className="audit-list">
          {grouped.map((g) => {
            const st = regen[g.spr]?.state || "idle";
            const cell = regen[g.spr]?.cell;
            const worstSevLabel = g.worstSev === 0 ? "MAJOR" : g.worstSev === 1 ? "MINOR" : "OBSERVATION";
            const worstSevClass = g.worstSev === 0 ? "sev-major" : g.worstSev === 1 ? "sev-minor" : "sev-obs";
            return (
              <li key={g.spr} className="audit-group">
                <details className="audit-accordion">
                  <summary className="audit-group-head">
                    <span className={`audit-sev ${worstSevClass}`}>{worstSevLabel}</span>
                    <code className="audit-spr-tag">{g.spr}</code>
                    <span className="audit-count">{g.findings.length} {g.findings.length === 1 ? t("audit_finding") : t("audit_findings")}</span>
                    <button
                      className="btn-regen"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); runRegen(g.spr); }}
                      disabled={st === "busy"}
                      aria-busy={st === "busy"}
                      title={t("btn_regenerate_title")}
                    >
                      {st === "busy" ? (<><span className="spinner-sm" /> {t("btn_regenerating")}</>) :
                       st === "done" ? (<>{t("btn_regenerated")}</>) :
                       st === "error" ? (<>{t("btn_regen_retry")}</>) :
                       (<>{t("btn_regenerate")}</>)}
                    </button>
                  </summary>
                  <div className="audit-accordion-body">
                {g.findings.map((f, i) => (
                  <div key={i} className={`audit-finding ${sevColor(f.severity)}`}>
                    <span className="audit-sev">{f.severity.toUpperCase()}</span>
                    <code className="audit-code">{f.code}</code>
                    <span className="audit-msg">
                      {f.message}
                      {f.message_tr && (
                        <span className="audit-msg-tr"> {f.message_tr}</span>
                      )}
                    </span>
                  </div>
                ))}
                {regen[g.spr]?.err && <div className="error">{t("err_prefix")}: {regen[g.spr]?.err}</div>}
                {cell && (
                  <div className="regen-output">
                    <div className="regen-label">{t("regen_new_en")}</div>
                    <p className="regen-text">{cell.generated_text}</p>
                    {cell.generated_text_tr && (
                      <>
                        <div className="regen-label">{t("regen_new_tr")}</div>
                        <p className="regen-text">{cell.generated_text_tr}</p>
                      </>
                    )}
                    <div className="regen-meta">
                      {t("regen_standards")} {(cell.standards_applied || []).join(", ") || "—"}
                    </div>
                    {cell.sources && cell.sources.length > 0 && (
                      <details className="cell-sources">
                        <summary>
                          🔍 {cell.sources.length} {t("cell_sources_summary")}
                        </summary>
                        <ul className="cell-sources-list">
                          {cell.sources.map((src, si) => (
                            <li key={si}>
                              <span className="src-badge">
                                {src.category || src.regulation || "?"}
                              </span>
                              {src.standard && <strong>{src.standard}{src.version ? ` ${src.version}` : ""}</strong>}
                              {src.heading && <span className="src-heading"> — {src.heading}</span>}
                              {src.content && (
                                <p className="src-snippet">{src.content.slice(0, 240)}{src.content.length > 240 ? "…" : ""}</p>
                              )}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
                  </div>
                </details>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="hint">{t("audit_no_findings")}</p>
      )}
    </div>
  );
}

// IntroStep — wizard'in ilk ekranı. 3 textbox + müşteri dropdown + "İlerle"
/** DescribeStep — kapak ile sorular arasindaki ara adim.
 *
 * NEDEN: gerekce metinlerini ureten yapay zeka, cihazi yalnizca sinif/temas/
 * sterilite gibi KUTULANMIS alanlardan taniyordu. Serbest metin alani
 * (intended_use_text) sistemde zaten vardi ve prompt'a "Intended Use" olarak
 * giriyordu, ama panelde YAZILACAK BIR YER YOKTU — yalnizca hazir urun
 * sablonlari dolduruyordu. Bu ekran o alani kullaniciya acar.
 *
 * Alan ZORUNLU DEGILDIR: bos birakilip gecilebilir. Doldurulmasi gerekceleri
 * urune ozgu hale getirir (musteri talebi, 2026-08-07).
 */
function DescribeStep({
  value, onChange, onProceed, t,
}: {
  value: string;
  onChange: (v: string) => void;
  onProceed: () => void;
  t: TFn;
}) {
  return (
    <div className="wizard wizard-intro">
      <div className="wizard-card">
        <h3>{t("wizard_describe_title")}</h3>
        <p className="hint">{t("wizard_describe_note")}</p>

        <textarea
          className="describe-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t("wizard_describe_placeholder")}
          rows={7}
          aria-label={t("wizard_describe_title")}
        />

        <div className="describe-foot">
          <span className="hint">{t("wizard_describe_optional")}</span>
          <span className="describe-count">{value.trim().length} {t("wizard_describe_chars")}</span>
        </div>

        <div className="wizard-nav" style={{ marginTop: 18, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {/* Zorunlu olmadigi icin buton HICBIR KOSULDA devre disi degil. */}
          <button className="btn-pri" onClick={onProceed}>
            {t("wizard_intro_proceed")} →
          </button>
        </div>
      </div>
    </div>
  );
}

function IntroStep({
  profile, customers, onChange, onProceed, sessionId, err, t,
}: {
  profile: ProductProfile;
  customers: CustomerOption[];
  onChange: (patch: Partial<ProductProfile>) => void;
  onProceed: () => void;
  sessionId: string | null;
  err: string | null;
  t: TFn;
}) {
  const selectedCustomer = customers.find((c) => c.id === profile.customer_id);

  // Seçim yoksa kendi firmayı (Ayarlar > Genel) varsayılan yap.
  useEffect(() => {
    if (profile.customer_id) return;
    const org = customers.find((c) => c.id === "__org__");
    if (org) onChange({ customer_id: org.id, customer_name: org.name, customer_logo_data_url: org.logo_data_url || "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  function onPickCustomer(id: string) {
    const c = customers.find((x) => x.id === id);
    if (!c) {
      onChange({ customer_id: undefined, customer_name: undefined, customer_logo_data_url: undefined });
      return;
    }
    onChange({
      customer_id: c.id,
      customer_name: c.name,
      customer_logo_data_url: c.logo_data_url || "",
    });
  }

  return (
    <div className="wizard wizard-intro">
      <div className="wizard-card">
        <h3>{t("wizard_intro_title")}</h3>
        <p className="hint">{t("wizard_intro_hint")}</p>

        <label className="form-row" style={{ display: "block", marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontWeight: 500 }}>{t("wizard_intro_product_name")}</div>
          <input
            type="text"
            value={profile.product_name || ""}
            onChange={(e) => onChange({ product_name: e.target.value })}
            style={{ width: "100%" }}
          />
        </label>

        <label className="form-row" style={{ display: "block", marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontWeight: 500 }}>{t("wizard_intro_risk_class")}</div>
          <input
            type="text"
            value={profile.risk_class_display || ""}
            onChange={(e) => onChange({ risk_class_display: e.target.value })}
            style={{ width: "100%" }}
          />
        </label>

        <label className="form-row" style={{ display: "block", marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontWeight: 500 }}>{t("wizard_intro_doc_code")}</div>
          <input
            type="text"
            value={profile.gspr_doc_code || ""}
            onChange={(e) => onChange({ gspr_doc_code: e.target.value })}
            style={{ width: "100%" }}
          />
        </label>

        <label className="form-row" style={{ display: "block", marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontWeight: 500 }}>{t("wizard_intro_customer")}</div>
          <select
            value={profile.customer_id || ""}
            onChange={(e) => onPickCustomer(e.target.value)}
            style={{ width: "100%" }}
          >
            <option value="">{t("wizard_intro_customer_none")}</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {customers.length === 0 && (
            <div className="hint" style={{ marginTop: 6 }}>{t("wizard_intro_no_customers")}</div>
          )}
        </label>

        {/* Önizleme: kapak sayfası mockup'ı */}
        <div className="intro-preview" style={{
          marginTop: 18,
          padding: "24px 20px",
          border: "1px dashed var(--border)",
          borderRadius: 8,
          textAlign: "center",
          background: "var(--bg-mute)",
        }}>
          {selectedCustomer?.logo_data_url && (
            <img
              src={selectedCustomer.logo_data_url}
              alt={selectedCustomer.name}
              style={{ maxHeight: 80, maxWidth: 200, objectFit: "contain", marginBottom: 12 }}
            />
          )}
          {selectedCustomer && (
            <div style={{ fontSize: 14, color: "var(--fg-mute)", marginBottom: 16 }}>
              {selectedCustomer.name}
            </div>
          )}
          {profile.product_name && (
            <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
              {profile.product_name}
            </div>
          )}
          {profile.risk_class_display && (
            <div style={{ fontSize: 14, marginBottom: 4 }}>{profile.risk_class_display}</div>
          )}
          {profile.gspr_doc_code && (
            <div style={{ fontSize: 13, color: "var(--fg-mute)", marginBottom: 12 }}>
              {profile.gspr_doc_code}
            </div>
          )}
          <div style={{ fontSize: 13, fontWeight: 500, marginTop: 8 }}>
            General Safety and Performance Requirements
          </div>
          <div style={{ fontSize: 11, color: "var(--fg-mute)" }}>
            Regulation (EU) 2017/745 — Annex I
          </div>
        </div>

        {err && <div className="error" style={{ marginTop: 12 }}>{t("err_prefix")}: {err}</div>}

        <div className="wizard-nav" style={{ marginTop: 18, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            type="button"
            className="btn-sec"
            disabled={!profile.product_name?.trim()}
            title={t("wizard_intro_preview_cover")}
            onClick={async () => {
              try {
                // Once cover bilgilerini DB'ye yaz (Ilerle olmadan onizleme alabilir)
                const id = sessionId || (await createSession()).id;
                await patchSession(id, {
                  product_name: profile.product_name?.trim(),
                  risk_class_display: profile.risk_class_display?.trim(),
                  gspr_doc_code: profile.gspr_doc_code?.trim(),
                  customer_id: profile.customer_id,
                  customer_name: profile.customer_name,
                  customer_logo_data_url: profile.customer_logo_data_url,
                });
                // Yeni sekmede sadece kapak DOCX indir
                window.open(
                  `/api/v1/gspr/sessions/${id}/document.docx?llm=false&chapter=0`,
                  "_blank",
                  "noopener,noreferrer"
                );
              } catch (e) {
                // sessizce — kullanici tekrar deneyebilir
                console.error("kapak onizleme hatasi", e);
              }
            }}
          >
            📄 {t("wizard_intro_preview_cover")}
          </button>
          <button
            className="btn-pri"
            disabled={!profile.product_name?.trim()}
            onClick={onProceed}
          >
            {t("wizard_intro_proceed")} →
          </button>
        </div>
      </div>
    </div>
  );
}
